import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';
import { Button } from '@/components/ui/Button';
import { Input } from '@medikei/ui';
import {
  DollarSign,
  Clock,
  Users,
  X,
  ShieldCheck,
  Key,
  Plus,
  Trash2,
  Play,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Home,
  Zap,
  ExternalLink,
  Pencil,
  Stethoscope,
  RotateCcw,
  CheckCircle2,
  CreditCard,
  Scale,
  FileText,
  Check,
  Save,
  Award,
  ArrowUp,
  ArrowDown,
  Star,
  Sparkles,
  Pin,
  Search,
  MessageSquare,
  Send,
  Copy,
  Smartphone,
  Terminal,
  Activity,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { adminApi } from '@/api/http-client';
import { paymentsApi } from '@/api/payments-api';
import { cn } from '@/utils/cn';
import { toast } from '@/components/ui/use-toast';

interface PlatformConfig {
  id: string;
  key: string;
  label: string;
  description: string | null;
  category: string;
  value: any;
  isActive: boolean;
  updatedAt: string;
}

interface SpecialtyPrice {
  id: string;
  specialtyId: string;
  expressaPrice: number;
  scheduledPrice: number;
  inPersonPrice?: number;
  homeCarePrice?: number;
  durationMinutes: number;
  specialty: {
    id: string;
    name: string;
  };
  pricing?: {
    telemedicine?: {
      price: number;
      directCost?: number;
      commissionPercent?: number;
      taxPercent?: number;
      netDoctorAmount?: number;
      marginPercent?: number;
    };
    inPerson?: {
      price: number;
      directCost?: number;
      commissionPercent?: number;
      taxPercent?: number;
      netDoctorAmount?: number;
      marginPercent?: number;
    };
    homeCare?: {
      price: number;
      directCost?: number;
      commissionPercent?: number;
      taxPercent?: number;
      netDoctorAmount?: number;
      marginPercent?: number;
    };
    durationMinutes?: number;
  };
}

interface ConfigsResponse {
  data: PlatformConfig[];
}

interface SpecialtyPricesResponse {
  data: SpecialtyPrice[];
}

type TabType = 'pricing' | 'pagbank' | 'priority' | 'cancellation' | 'consultation' | 'crm' | 'whatsapp';

export function AdminConfigPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabType) || 'pricing';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') as TabType | null;
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Estados para PagBank & Parcelamento
  const [simulationAmount, setSimulationAmount] = useState<number>(180);
  const [installmentForm, setInstallmentForm] = useState<{
    enabled: boolean;
    enableCreditCard: boolean;
    enableDebitCard: boolean;
    debitRatePercent: number;
    maxInstallments: number;
    interestFreeInstallments: number;
    minInstallmentAmount: number;
    minTotalAmountForInstallments: number;
    monthlyInterestRate: number;
    ratesByInstallment: Record<number, number>;
    passInterestToCustomer: boolean;
  }>({
    enabled: true,
    enableCreditCard: true,
    enableDebitCard: true,
    debitRatePercent: 1.99,
    maxInstallments: 6,
    interestFreeInstallments: 3,
    minInstallmentAmount: 30,
    minTotalAmountForInstallments: 50,
    monthlyInterestRate: 2.99,
    ratesByInstallment: {
      2: 2.49,
      3: 2.99,
      4: 3.29,
      5: 3.59,
      6: 3.99,
      7: 4.29,
      8: 4.59,
      9: 4.89,
      10: 5.19,
      11: 5.49,
      12: 5.99,
    },
    passInterestToCustomer: true,
  });

  // Estados para Ordem de Prioridade na Vitrine (Médicos Parceiros)
  const [doctorPriorityForm, setDoctorPriorityForm] = useState<{
    enabled: boolean;
    priorityList: Array<{ doctorId: string; order: number; badge?: string; isPinned: boolean }>;
  }>({
    enabled: true,
    priorityList: [],
  });
  const [doctorSearchToAdd, setDoctorSearchToAdd] = useState('');

  const { data: priorityData, isLoading: priorityLoading } = useQuery({
    queryKey: ['admin', 'config', 'doctor-priority'],
    queryFn: () => adminApi.getDoctorPriorityConfig(),
  });

  useEffect(() => {
    if (priorityData?.config) {
      setDoctorPriorityForm({
        enabled: priorityData.config.enabled ?? true,
        priorityList: priorityData.config.priorityList || [],
      });
    }
  }, [priorityData]);

  const updatePriorityMutation = useMutation({
    mutationFn: (data: typeof doctorPriorityForm) => adminApi.updateDoctorPriorityConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'doctor-priority'] });
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
      alert('Ordem e prioridade dos médicos parceiros salva com sucesso! A vitrine da página inicial já está atualizada.');
    },
  });

  const { data: installmentConfigData, isLoading: installmentLoading } = useQuery({
    queryKey: ['admin', 'config', 'pagbank-installments'],
    queryFn: () => paymentsApi.getPagBankInstallmentConfig(),
  });

  useEffect(() => {
    if (installmentConfigData) {
      const rawRates = installmentConfigData.ratesByInstallment || {};
      const ratesByInstallment: Record<number, number> = {};
      for (let i = 2; i <= 12; i++) {
        ratesByInstallment[i] = rawRates[i] !== undefined
          ? Number(rawRates[i])
          : (installmentConfigData.monthlyInterestRate ?? 2.99);
      }

      setInstallmentForm({
        enabled: installmentConfigData.enabled ?? true,
        enableCreditCard: installmentConfigData.enableCreditCard ?? (installmentConfigData.enabled ?? true),
        enableDebitCard: installmentConfigData.enableDebitCard ?? true,
        debitRatePercent: installmentConfigData.debitRatePercent ?? 1.99,
        maxInstallments: installmentConfigData.maxInstallments ?? 6,
        interestFreeInstallments: installmentConfigData.interestFreeInstallments ?? 3,
        minInstallmentAmount: installmentConfigData.minInstallmentAmount ?? 30,
        minTotalAmountForInstallments: installmentConfigData.minTotalAmountForInstallments ?? 50,
        monthlyInterestRate: installmentConfigData.monthlyInterestRate ?? 2.99,
        ratesByInstallment,
        passInterestToCustomer: installmentConfigData.passInterestToCustomer ?? true,
      });
    }
  }, [installmentConfigData]);

  const updateInstallmentMutation = useMutation({
    mutationFn: (data: typeof installmentForm) => paymentsApi.updatePagBankInstallmentConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config'] });
      queryClient.invalidateQueries({ queryKey: ['pagbank'] });
      alert('Regras de parcelamento e taxas PagBank salvas com sucesso! As alterações já estão ativas para todas as consultas e agendamentos.');
    },
  });

  const { data: configsData } = useQuery<ConfigsResponse>({
    queryKey: ['admin', 'config', 'platform'],
    queryFn: async () => {
      const res = await fetch('/api/v1/admin/config/platform', {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (!res.ok) throw new Error('Failed to fetch configs');
      return res.json();
    },
  });

  const { data: specialtyPricesData, isLoading: pricesLoading } = useQuery<SpecialtyPricesResponse>({
    queryKey: ['admin', 'config', 'pricing'],
    queryFn: async () => {
      const res = await fetch('/api/v1/admin/config/pricing', {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (!res.ok) throw new Error('Failed to fetch specialty prices');
      return res.json();
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const res = await fetch(`/api/v1/admin/config/platform/${key}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ value }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.message || 'Falha ao atualizar configuração');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'platform'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'config'] });
      setEditingKey(null);
      toast({
        title: 'Configuração atualizada com sucesso',
        description: 'O novo parâmetro da plataforma foi salvo.',
        variant: 'default',
      });
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao salvar configuração',
        description: err?.message || 'Tente novamente.',
        variant: 'error',
      });
    },
  });

  const configs = configsData?.data || [];
  const specialtyPrices = specialtyPricesData?.data || [];

  // Estado e queries para Precificação Financeira do Plantão Express 24h
  const [isExpressModalOpen, setIsExpressModalOpen] = useState(false);
  const [expressPriceForm, setExpressPriceForm] = useState({
    price: 150,
    directCost: 25,
    commissionPercent: 15,
    taxPercent: 6,
  });

  const { data: expressPricingData } = useQuery({
    queryKey: ['admin', 'config', 'express-pricing'],
    queryFn: async () => {
      const res = await adminApi.getExpressPricing();
      // httpClient desempacota automaticamente se a resposta contiver .data
      const payload = (res as any)?.price !== undefined ? res : (res as any)?.data;
      return payload || { price: 150, directCost: 25, commissionPercent: 15, taxPercent: 6 };
    },
  });

  useEffect(() => {
    if (expressPricingData) {
      setExpressPriceForm({
        price: Number(expressPricingData.price ?? 150),
        directCost: Number(expressPricingData.directCost ?? 25),
        commissionPercent: Number(expressPricingData.commissionPercent ?? 15),
        taxPercent: Number(expressPricingData.taxPercent ?? 6),
      });
    }
  }, [expressPricingData]);

  const updateExpressPricingMutation = useMutation({
    mutationFn: async (payload: any) => {
      return adminApi.updateExpressPricing(payload);
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'express-pricing'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'config'] });
      const payload = res?.price !== undefined ? res : res?.data;
      if (payload) {
        setExpressPriceForm({
          price: Number(payload.price ?? 150),
          directCost: Number(payload.directCost ?? 25),
          commissionPercent: Number(payload.commissionPercent ?? 15),
          taxPercent: Number(payload.taxPercent ?? 6),
        });
      }
      setIsExpressModalOpen(false);
    },
  });

  const currentExpressFinancial = useMemo(() => {
    const p = Math.max(0, Number(expressPriceForm.price) || 0);
    const cost = Math.max(0, Number(expressPriceForm.directCost) || 0);
    const commPct = Math.max(0, Number(expressPriceForm.commissionPercent) || 0);
    const taxPct = Math.max(0, Number(expressPriceForm.taxPercent) || 0);

    const commissionVal = Math.round(p * (commPct / 100) * 100) / 100;
    const taxVal = Math.round(p * (taxPct / 100) * 100) / 100;
    const netDoctor = Math.max(0, Math.round((p - cost - commissionVal - taxVal) * 100) / 100);
    const marginPct = p > 0 ? Math.round((netDoctor / p) * 10000) / 100 : 0;
    const platformRetained = Math.round((cost + commissionVal + taxVal) * 100) / 100;

    return {
      price: p,
      directCost: cost,
      commissionPercent: commPct,
      taxPercent: taxPct,
      commissionVal,
      taxVal,
      netDoctor,
      marginPct,
      platformRetained,
    };
  }, [expressPriceForm]);

  const getConfig = (key: string): PlatformConfig | undefined => {
    return configs.find((c) => c.key === key);
  };

  // Estado e mutações para Solicitações de Cancelamento e Estorno
  const [rejectModalId, setRejectModalId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [approveConfirmId, setApproveConfirmId] = useState<string | null>(null);

  const {
    data: cancelRequestsData,
    isLoading: cancelRequestsLoading,
  } = useQuery<{ data: any[] }>({
    queryKey: ['admin', 'config', 'cancellation-requests'],
    queryFn: async () => {
      const res = await fetch('/api/v1/admin/config/cancellation-requests', {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (!res.ok) throw new Error('Falha ao buscar solicitações');
      return res.json();
    },
    enabled: activeTab === 'cancellation',
  });

  const approveCancelMutation = useMutation({
    mutationFn: async ({ id, adminNote }: { id: string; adminNote?: string }) => {
      const res = await fetch(`/api/v1/admin/config/cancellation-requests/${id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ adminNote }),
      });
      if (!res.ok) throw new Error('Falha ao aprovar cancelamento');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'cancellation-requests'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'financial'] });
      setApproveConfirmId(null);
    },
  });

  const rejectCancelMutation = useMutation({
    mutationFn: async ({ id, adminNote }: { id: string; adminNote: string }) => {
      const res = await fetch(`/api/v1/admin/config/cancellation-requests/${id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ adminNote }),
      });
      if (!res.ok) throw new Error('Falha ao rejeitar cancelamento');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'cancellation-requests'] });
      setRejectModalId(null);
      setRejectReason('');
    },
  });

  const formatCurrency = (val: any) => {
    let numeric = 0;
    if (typeof val === 'number') {
      numeric = val;
    } else if (typeof val === 'string') {
      const parsed = parseFloat(val);
      numeric = isNaN(parsed) ? 0 : parsed;
    } else if (val && typeof val === 'object' && 'value' in val) {
      const parsed = parseFloat(val.value);
      numeric = isNaN(parsed) ? 0 : parsed;
    }
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numeric);
  };

  const formatPercent = (value: any) => {
    if (typeof value === 'number') return `${value}%`;
    if (value?.percent) return `${value.percent}%`;
    return 'N/A';
  };

  const handleEditConfig = (config: PlatformConfig) => {
    setEditingKey(config.key);
    const v = config.value as any;
    if (v !== undefined && v !== null) {
      if (typeof v === 'number') {
        setEditValue(String(v));
      } else if (v.minutes !== undefined) {
        setEditValue(String(v.minutes));
      } else if (v.seconds !== undefined) {
        setEditValue(String(v.seconds));
      } else if (v.hours !== undefined) {
        setEditValue(String(v.hours));
      } else if (v.percent !== undefined) {
        setEditValue(String(v.percent));
      } else if (v.value !== undefined) {
        setEditValue(String(v.value));
      } else if (typeof v === 'object') {
        setEditValue(JSON.stringify(v));
      } else {
        setEditValue(String(v));
      }
    } else {
      setEditValue('');
    }
  };

  const handleSaveConfig = () => {
    if (!editingKey) return;
    const original = getConfig(editingKey);
    const origVal = original?.value as any;
    const cleanStr = editValue.replace(',', '.').trim();
    const num = Number(cleanStr);

    let valueToSave: any;
    if (!isNaN(num) && cleanStr !== '') {
      if (origVal && typeof origVal === 'object') {
        if ('minutes' in origVal || editingKey.includes('_MINUTES') || editingKey.includes('_DURATION')) {
          valueToSave = { ...origVal, minutes: Math.round(num) };
        } else if ('seconds' in origVal || editingKey.includes('_SECONDS')) {
          valueToSave = { ...origVal, seconds: Math.round(num) };
        } else if ('hours' in origVal || editingKey.includes('_HOURS')) {
          valueToSave = { ...origVal, hours: Math.round(num) };
        } else if ('percent' in origVal || editingKey.includes('_PERCENT')) {
          valueToSave = { ...origVal, percent: num };
        } else if ('value' in origVal) {
          valueToSave = { ...origVal, value: num };
        } else {
          valueToSave = num;
        }
      } else if (editingKey.includes('_MINUTES') || editingKey.includes('_DURATION')) {
        valueToSave = { minutes: Math.round(num) };
      } else if (editingKey.includes('_SECONDS')) {
        valueToSave = { seconds: Math.round(num) };
      } else if (editingKey.includes('_PERCENT')) {
        valueToSave = { percent: num };
      } else {
        valueToSave = num;
      }
    } else {
      try {
        valueToSave = JSON.parse(editValue);
      } catch {
        valueToSave = editValue;
      }
    }

    updateConfigMutation.mutate({ key: editingKey, value: valueToSave });
  };

  // CRM Configuration Query & Mutations
  const { data: crmConfigData, isLoading: crmLoading } = useQuery({
    queryKey: ['admin', 'config', 'crm'],
    queryFn: () => adminApi.getCrmConfig(),
  });

  const crmConfig = useMemo(() => {
    const raw = (crmConfigData as any)?.data !== undefined ? (crmConfigData as any).data : crmConfigData;
    return {
      enabled: Boolean(raw?.enabled),
      keys: Array.isArray(raw?.keys) ? (raw.keys as any[]) : [],
    };
  }, [crmConfigData]);

  const toggleCrmMutation = useMutation({
    mutationFn: (enabled: boolean) => adminApi.toggleCrmValidation(enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'crm'] });
    },
  });

  const addCrmKeyMutation = useMutation({
    mutationFn: (data: { name: string; key: string; quota?: number }) => adminApi.addCrmKey(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'crm'] });
      setIsAddingKey(false);
      setNewKeyName('');
      setNewKeyValue('');
      setNewKeyQuota(100);
    },
  });

  const updateCrmKeyMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; isActive?: boolean; quota?: number }) =>
      adminApi.updateCrmKey(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'crm'] });
    },
  });

  const deleteCrmKeyMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteCrmKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'crm'] });
    },
  });

  // Add Key Form state
  const [isAddingKey, setIsAddingKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [newKeyQuota, setNewKeyQuota] = useState(100);

  // Test Sandbox state
  const [testCrm, setTestCrm] = useState('123456');
  const [testUf, setTestUf] = useState('SP');
  const [testSelectedKeyId, setTestSelectedKeyId] = useState<string>('auto');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const handleRunCrmTest = async () => {
    setTestRunning(true);
    setTestResult(null);
    setTestError(null);
    try {
      const selectedKey = testSelectedKeyId !== 'auto'
        ? crmConfig.keys.find((k) => k.id === testSelectedKeyId)?.key
        : undefined;
      const res = await adminApi.testCrmKey({
        key: selectedKey,
        crm: testCrm,
        crmState: testUf,
      });
      setTestResult(res);
    } catch (err: any) {
      setTestError(err?.message || 'Falha ao executar teste da API Consulta CRM');
    } finally {
      setTestRunning(false);
    }
  };

  // Estados e mutações para Chatwoot & WhatsApp Cloud API
  const [chatwootForm, setChatwootForm] = useState({
    frontendUrl: 'https://chat.medikei.com.br',
    apiAccessToken: '',
    accountId: '1',
    inboxId: 1,
    phoneNumberId: '',
    businessAccountId: '',
    verifiedPhoneNumber: '',
    permanentAccessToken: '',
    webhookVerifyToken: '',
    templateName: 'envio_documento_paciente',
    isEnabled: true,
  });

  const [testPhone, setTestPhone] = useState('');
  const [testDocType, setTestDocType] = useState<'Receita Médica' | 'Atestado' | 'Pedido de Exame' | 'Prontuário'>('Receita Médica');
  const [testPdfUrl, setTestPdfUrl] = useState('https://storage.medikei.com.br/demo/receita-exemplo.pdf');
  const [testPatientName, setTestPatientName] = useState('Carlos Silva');
  const [testDoctorName, setTestDoctorName] = useState('Dra. Camila Ramos');
  const [isTestingChatwootConnection, setIsTestingChatwootConnection] = useState(false);
  const [isTestingChatwootDispatch, setIsTestingChatwootDispatch] = useState(false);
  const [chatwootConnectionStatus, setChatwootConnectionStatus] = useState<{ success?: boolean; message?: string } | null>(null);
  const [chatwootDispatchResult, setChatwootDispatchResult] = useState<any>(null);
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);

  const { data: chatwootConfigData } = useQuery({
    queryKey: ['admin', 'config', 'chatwoot'],
    queryFn: async () => {
      const res = await adminApi.getChatwootConfig();
      return (res as any)?.data !== undefined ? (res as any).data : res;
    },
  });

  useEffect(() => {
    if (chatwootConfigData) {
      setChatwootForm({
        frontendUrl: chatwootConfigData.frontendUrl || 'https://chat.medikei.com.br',
        apiAccessToken: chatwootConfigData.apiAccessToken || '',
        accountId: String(chatwootConfigData.accountId || '1'),
        inboxId: Number(chatwootConfigData.inboxId || 1),
        phoneNumberId: chatwootConfigData.phoneNumberId || '',
        businessAccountId: chatwootConfigData.businessAccountId || '',
        verifiedPhoneNumber: chatwootConfigData.verifiedPhoneNumber || '',
        permanentAccessToken: chatwootConfigData.permanentAccessToken || '',
        webhookVerifyToken: chatwootConfigData.webhookVerifyToken || '',
        templateName: chatwootConfigData.templateName || 'envio_documento_paciente',
        isEnabled: chatwootConfigData.isEnabled ?? true,
      });
    }
  }, [chatwootConfigData]);

  const updateChatwootMutation = useMutation({
    mutationFn: (data: typeof chatwootForm) => adminApi.updateChatwootConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'chatwoot'] });
      toast({
        title: 'Configurações de WhatsApp salvas!',
        description: 'Parâmetros do Chatwoot e da Meta gravados com sucesso.',
        variant: 'default',
      });
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao salvar configurações',
        description: err?.message || 'Tente novamente.',
        variant: 'error',
      });
    },
  });

  const handleTestChatwootConnection = async () => {
    setIsTestingChatwootConnection(true);
    setChatwootConnectionStatus(null);
    try {
      const res = await adminApi.testChatwootConnection();
      setChatwootConnectionStatus(res);
      toast({
        title: res.success ? 'Conexão estabelecida!' : 'Falha na conexão',
        description: res.message,
        variant: res.success ? 'default' : 'error',
      });
    } catch (err: any) {
      setChatwootConnectionStatus({
        success: false,
        message: err?.message || 'Falha ao conectar com Chatwoot',
      });
    } finally {
      setIsTestingChatwootConnection(false);
    }
  };

  const handleTestChatwootDispatch = async () => {
    if (!testPhone.trim()) {
      toast({
        title: 'Informe o telefone',
        description: 'Digite o número do paciente no formato DDI+DDD+Número (ex: +5531988887777).',
        variant: 'error',
      });
      return;
    }

    setIsTestingChatwootDispatch(true);
    setChatwootDispatchResult(null);
    try {
      const cleanPhone = testPhone.startsWith('+') ? testPhone : `+55${testPhone.replace(/\D/g, '')}`;
      const res = await adminApi.testChatwootDispatch({
        telefonePaciente: cleanPhone,
        nomePaciente: testPatientName.trim() || 'Paciente Teste',
        nomeMedico: testDoctorName.trim() || 'Dra. Médica Teste',
        tipoDocumento: testDocType,
        urlPdfDocumento: testPdfUrl.trim(),
        pacienteId: 'test_paciente_id_sandbox',
      });
      setChatwootDispatchResult(res);
      toast({
        title: 'Documento despachado!',
        description: `Mensagem enviada com sucesso ao Chatwoot (ID: ${(res as any)?.messageId || 'OK'}).`,
        variant: 'default',
      });
    } catch (err: any) {
      setChatwootDispatchResult({
        error: true,
        message: err?.message || 'Erro ao despachar documento via Chatwoot',
      });
      toast({
        title: 'Erro no envio do documento',
        description: err?.message || 'Verifique as credenciais e o template na Meta.',
        variant: 'error',
      });
    } finally {
      setIsTestingChatwootDispatch(false);
    }
  };

  const handleCopySnippet = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSnippet(key);
    setTimeout(() => setCopiedSnippet(null), 2000);
  };

  const simulatedInstallments = useMemo(() => {
    const valorReais = Number(simulationAmount) || 0;
    const options: Array<{
      numero: number;
      valorParcelaReais: number;
      valorTotalReais: number;
      temJuros: boolean;
      taxaPercent: number;
      descricao: string;
    }> = [];

    options.push({
      numero: 1,
      valorParcelaReais: valorReais,
      valorTotalReais: valorReais,
      temJuros: false,
      taxaPercent: 0,
      descricao: `1x de R$ ${valorReais.toFixed(2).replace('.', ',')} (sem juros)`,
    });

    if (!installmentForm.enabled || !installmentForm.enableCreditCard || valorReais < installmentForm.minTotalAmountForInstallments) {
      return options;
    }

    const maxN = Math.min(Number(installmentForm.maxInstallments) || 6, 12);

    for (let n = 2; n <= maxN; n++) {
      const temJuros = installmentForm.passInterestToCustomer && n > (Number(installmentForm.interestFreeInstallments) || 1);
      const taxaParcelaPercent =
        installmentForm.ratesByInstallment?.[n] !== undefined
          ? Number(installmentForm.ratesByInstallment[n])
          : (installmentForm.monthlyInterestRate || 2.99);
      const taxaDecimal = taxaParcelaPercent / 100;

      if (!temJuros) {
        const parcelaReais = Math.round((valorReais / n) * 100) / 100;
        if (parcelaReais < installmentForm.minInstallmentAmount && n > 2) {
          break;
        }
        options.push({
          numero: n,
          valorParcelaReais: parcelaReais,
          valorTotalReais: valorReais,
          temJuros: false,
          taxaPercent: 0,
          descricao: `${n}x de R$ ${parcelaReais.toFixed(2).replace('.', ',')} (sem juros)`,
        });
      } else {
        const p = valorReais;
        const i = taxaDecimal;
        const fator = (i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
        const parcelaReais = Math.round(p * fator * 100) / 100;
        const totalReais = Math.round(parcelaReais * n * 100) / 100;

        if (parcelaReais < installmentForm.minInstallmentAmount) {
          break;
        }

        options.push({
          numero: n,
          valorParcelaReais: parcelaReais,
          valorTotalReais: totalReais,
          temJuros: true,
          taxaPercent: taxaParcelaPercent,
          descricao: `${n}x de R$ ${parcelaReais.toFixed(2).replace('.', ',')} (Total: R$ ${totalReais.toFixed(2).replace('.', ',')})`,
        });
      }
    }

    return options;
  }, [simulationAmount, installmentForm]);

  // Funções Auxiliares para Ordem de Prioridade dos Médicos Parceiros
  const moveDoctorPriority = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= doctorPriorityForm.priorityList.length) return;
    const newList = [...doctorPriorityForm.priorityList];
    const [moved] = newList.splice(index, 1);
    if (moved) {
      newList.splice(targetIndex, 0, moved);
      const reordered = newList.map((item, idx) => ({ ...item, order: idx + 1 }));
      setDoctorPriorityForm({ ...doctorPriorityForm, priorityList: reordered });
    }
  };

  const togglePinDoctor = (doctorId: string) => {
    const newList = doctorPriorityForm.priorityList.map((item) =>
      item.doctorId === doctorId ? { ...item, isPinned: !item.isPinned } : item
    );
    setDoctorPriorityForm({ ...doctorPriorityForm, priorityList: newList });
  };

  const updateDoctorBadge = (doctorId: string, badge?: string) => {
    const newList = doctorPriorityForm.priorityList.map((item) =>
      item.doctorId === doctorId ? { ...item, badge: badge ? badge.trim() : undefined } : item
    );
    setDoctorPriorityForm({ ...doctorPriorityForm, priorityList: newList });
  };

  const removeDoctorFromPriority = (doctorId: string) => {
    const newList = doctorPriorityForm.priorityList
      .filter((item) => item.doctorId !== doctorId)
      .map((item, idx) => ({ ...item, order: idx + 1 }));
    setDoctorPriorityForm({ ...doctorPriorityForm, priorityList: newList });
  };

  const addDoctorToPriority = (doctorId: string) => {
    if (doctorPriorityForm.priorityList.some((item) => item.doctorId === doctorId)) return;
    const nextOrder = doctorPriorityForm.priorityList.length + 1;
    const newItem = {
      doctorId,
      order: nextOrder,
      badge: 'Destaque Parceiro',
      isPinned: false,
    };
    setDoctorPriorityForm({
      ...doctorPriorityForm,
      priorityList: [...doctorPriorityForm.priorityList, newItem],
    });
  };

  const tabs = [
    { id: 'pricing' as TabType, label: 'Preços', icon: DollarSign },
    { id: 'pagbank' as TabType, label: 'PagBank & Parcelas', icon: CreditCard },
    { id: 'priority' as TabType, label: 'Ordem na Vitrine', icon: Award },
    { id: 'cancellation' as TabType, label: 'Cancelamento', icon: X },
    { id: 'consultation' as TabType, label: 'Consulta', icon: Clock },
    { id: 'crm' as TabType, label: 'Validação CRM', icon: ShieldCheck },
    { id: 'whatsapp' as TabType, label: 'WhatsApp & Chatwoot', icon: MessageSquare },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Configurações</h1>
      </div>

      <div className="flex gap-2 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setSearchParams({ tab: tab.id });
            }}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'pricing' && (
        <div className="space-y-6">
          {/* Card 1: Consulta Expressa (Plantão 24h) - Preço Global Único com Decomposição Financeira */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card">
            <CardHeader className="border-b border-border/80 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                      <span>Consulta Expressa (Plantão 24h)</span>
                      <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20 uppercase tracking-wide">
                        Preço Global Único
                      </span>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Valor padrão cobrado para qualquer atendimento imediato na fila virtual do plantão
                    </p>
                  </div>
                </div>

                <Button
                  onClick={() => setIsExpressModalOpen(true)}
                  className="rounded-xl font-bold text-xs h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs gap-1.5 cursor-pointer self-start sm:self-auto"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  <span>Editar Precificação Financeira</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="p-3.5 rounded-2xl bg-muted/40 border border-border text-left">
                  <span className="text-[11px] font-semibold text-muted-foreground block uppercase">Preço ao Paciente</span>
                  <p className="text-xl font-extrabold text-foreground mt-0.5">
                    {formatCurrency(currentExpressFinancial.price)}
                  </p>
                  <span className="text-[10px] text-muted-foreground">Valor de venda</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-muted/40 border border-border text-left">
                  <span className="text-[11px] font-semibold text-muted-foreground block uppercase">Custo Direto</span>
                  <p className="text-xl font-extrabold text-foreground mt-0.5">
                    {formatCurrency(currentExpressFinancial.directCost)}
                  </p>
                  <span className="text-[10px] text-muted-foreground">Infraestrutura fixa</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-muted/40 border border-border text-left">
                  <span className="text-[11px] font-semibold text-muted-foreground block uppercase">Comissão Medikei</span>
                  <p className="text-xl font-extrabold text-primary mt-0.5">
                    {currentExpressFinancial.commissionPercent}%
                  </p>
                  <span className="text-[10px] text-muted-foreground">{formatCurrency(currentExpressFinancial.commissionVal)}</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-muted/40 border border-border text-left">
                  <span className="text-[11px] font-semibold text-muted-foreground block uppercase">Impostos</span>
                  <p className="text-xl font-extrabold text-foreground mt-0.5">
                    {currentExpressFinancial.taxPercent}%
                  </p>
                  <span className="text-[10px] text-muted-foreground">{formatCurrency(currentExpressFinancial.taxVal)}</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 text-left">
                  <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 block uppercase">Repasse Plantonista</span>
                  <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
                    {formatCurrency(currentExpressFinancial.netDoctor)}
                  </p>
                  <span className="text-[10px] text-emerald-700 dark:text-emerald-300">Lucro líquido do médico</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-muted/40 border border-border text-left">
                  <span className="text-[11px] font-semibold text-muted-foreground block uppercase">Margem Médica</span>
                  <p className="text-xl font-extrabold text-foreground mt-0.5">
                    {currentExpressFinancial.marginPct.toFixed(0)}%
                  </p>
                  <span className="text-[10px] text-muted-foreground">Rentabilidade líquida</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Matriz de Preços por Especialidade (Agendadas) */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card">
            <CardHeader className="border-b border-border/80 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-primary/10 text-primary border border-primary/20">
                    <Stethoscope className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground">
                      Matriz de Preços por Especialidade (Consultas Agendadas)
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Precificação das especialidades por Telemedicina, Presencial e Domiciliar
                    </p>
                  </div>
                </div>

                <Link to="/admin/especialidades">
                  <Button
                    variant="outline"
                    className="rounded-xl font-bold text-xs h-10 px-4 gap-1.5 cursor-pointer self-start sm:self-auto hover:bg-primary hover:text-white transition-all shadow-xs"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Gerenciar Especialidades &amp; Matriz Completa</span>
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {pricesLoading ? (
                <div className="py-12 text-center text-sm text-muted-foreground">Carregando matriz de preços...</div>
              ) : specialtyPrices.length === 0 ? (
                <p className="text-muted-foreground py-8 text-center text-sm">Nenhuma especialidade cadastrada</p>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-12 gap-3 font-bold text-xs text-muted-foreground border-b border-border pb-2.5 uppercase tracking-wider">
                    <div className="col-span-3">Especialidade</div>
                    <div className="col-span-3">Telemedicina</div>
                    <div className="col-span-3">Presencial</div>
                    <div className="col-span-2">Domiciliar</div>
                    <div className="col-span-1 text-right">Ação</div>
                  </div>

                  {specialtyPrices.map((price) => (
                    <div
                      key={price.specialtyId}
                      className="grid grid-cols-12 gap-3 items-center py-3 border-b border-border/60 hover:bg-muted/30 transition-colors rounded-xl px-2 text-xs"
                    >
                      <div className="col-span-3 font-bold text-foreground text-sm truncate" title={price.specialty.name}>
                        {price.specialty.name}
                        {price.durationMinutes ? <span className="block text-[11px] text-muted-foreground font-normal">{price.durationMinutes} min</span> : null}
                      </div>

                      <div className="col-span-3">
                        <span className="font-bold text-foreground">
                          {formatCurrency(price.scheduledPrice ?? price.pricing?.telemedicine?.price ?? 120)}
                        </span>
                        <span className="block text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                          Médico: {formatCurrency(price.pricing?.telemedicine?.netDoctorAmount ?? ((price.scheduledPrice ?? 120) * 0.7))}
                        </span>
                      </div>

                      <div className="col-span-3">
                        <span className="font-bold text-foreground">
                          {formatCurrency(price.inPersonPrice ?? price.pricing?.inPerson?.price ?? 180)}
                        </span>
                        <span className="block text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                          Médico: {formatCurrency(price.pricing?.inPerson?.netDoctorAmount ?? ((price.inPersonPrice ?? 180) * 0.7))}
                        </span>
                      </div>

                      <div className="col-span-2">
                        <span className="font-bold text-foreground">
                          {formatCurrency(price.homeCarePrice ?? price.pricing?.homeCare?.price ?? 250)}
                        </span>
                        <span className="block text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                          Médico: {formatCurrency(price.pricing?.homeCare?.netDoctorAmount ?? ((price.homeCarePrice ?? 250) * 0.7))}
                        </span>
                      </div>

                      <div className="col-span-1 text-right">
                        <Link
                          to="/admin/especialidades"
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline"
                        >
                          <span>Editar</span>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card 3: Consulta Domiciliar (Valores 100% Embutidos) */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card">
            <CardHeader className="border-b border-border/80 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                  <Home className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-foreground">
                    Consulta Domiciliar (Valores 100% Embutidos)
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Modelo simplificado e unificado de atendimento em casa
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide block">
                    Taxas de Deslocamento Embutidas
                  </span>
                  <p className="text-xs sm:text-sm text-foreground leading-relaxed max-w-2xl">
                    Conforme diretriz do sistema Medikei, todas as taxas de deslocamento, transporte e custos operacionais das consultas em domicílio são configurados e embutidos diretamente no valor de venda de cada especialidade no catálogo.
                  </p>
                </div>

                <Link to="/admin/especialidades" className="shrink-0">
                  <Button size="sm" variant="outline" className="rounded-xl text-xs font-bold gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Ver no Catálogo</span>
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Modal Financeiro da Consulta Expressa (Plantão 24h) - Idêntico ao modelo anexado */}
          <Dialog open={isExpressModalOpen} onOpenChange={(open) => !open && setIsExpressModalOpen(false)}>
            <DialogContent className="max-w-xl p-0 overflow-hidden rounded-3xl border border-border shadow-2xl bg-card">
              <DialogHeader className="px-6 py-5 border-b border-border bg-card">
                <DialogTitle className="text-xl font-extrabold text-foreground flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  <span>Precificação Financeira · Plantão Express 24h</span>
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  Configure o valor cobrado do paciente, custos de infraestrutura e repasse ao plantonista
                </DialogDescription>
              </DialogHeader>

              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Campo 1: Preço de venda */}
                  <div>
                    <label className="block text-xs font-bold text-foreground mb-1">
                      Preço de venda (R$)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={expressPriceForm.price}
                      onChange={(e) => setExpressPriceForm({ ...expressPriceForm, price: parseFloat(e.target.value) || 0 })}
                      className="rounded-xl h-11 text-sm font-semibold"
                    />
                    <span className="text-[10px] text-muted-foreground mt-0.5 block">
                      Valor total cobrado do paciente
                    </span>
                  </div>

                  {/* Campo 2: Custo direto */}
                  <div>
                    <label className="block text-xs font-bold text-foreground mb-1">
                      Custo direto (R$)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={expressPriceForm.directCost}
                      onChange={(e) => setExpressPriceForm({ ...expressPriceForm, directCost: parseFloat(e.target.value) || 0 })}
                      className="rounded-xl h-11 text-sm font-semibold"
                    />
                    <span className="text-[10px] text-muted-foreground mt-0.5 block">
                      Infraestrutura, gateway e telecom
                    </span>
                  </div>

                  {/* Campo 3: Comissão da plataforma (%) */}
                  <div>
                    <label className="block text-xs font-bold text-foreground mb-1">
                      Comissão da Plataforma (%)
                    </label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={expressPriceForm.commissionPercent}
                      onChange={(e) => setExpressPriceForm({ ...expressPriceForm, commissionPercent: parseFloat(e.target.value) || 0 })}
                      className="rounded-xl h-11 text-sm font-semibold"
                    />
                    <span className="text-[10px] text-muted-foreground mt-0.5 block">
                      Taxa Medikei: {formatCurrency(currentExpressFinancial.commissionVal)}
                    </span>
                  </div>

                  {/* Campo 4: Impostos (%) */}
                  <div>
                    <label className="block text-xs font-bold text-foreground mb-1">
                      Impostos (%)
                    </label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={expressPriceForm.taxPercent}
                      onChange={(e) => setExpressPriceForm({ ...expressPriceForm, taxPercent: parseFloat(e.target.value) || 0 })}
                      className="rounded-xl h-11 text-sm font-semibold"
                    />
                    <span className="text-[10px] text-muted-foreground mt-0.5 block">
                      Impostos retidos: {formatCurrency(currentExpressFinancial.taxVal)}
                    </span>
                  </div>
                </div>

                {/* Cards de Resultado Automático */}
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="p-4 rounded-2xl bg-muted/40 dark:bg-neutral-900 border border-border">
                    <span className="text-xs text-muted-foreground font-semibold block mb-1">
                      Repasse ao Plantonista
                    </span>
                    <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(currentExpressFinancial.netDoctor)}
                    </p>
                    <span className="text-[10px] text-muted-foreground mt-0.5 block">
                      Creditado na carteira após atendimento
                    </span>
                  </div>

                  <div className="p-4 rounded-2xl bg-muted/40 dark:bg-neutral-900 border border-border">
                    <span className="text-xs text-muted-foreground font-semibold block mb-1">
                      Margem do Médico
                    </span>
                    <p className="text-2xl font-black text-foreground">
                      {currentExpressFinancial.marginPct.toFixed(0)}%
                    </p>
                    <span className="text-[10px] text-muted-foreground mt-0.5 block">
                      Retenção da plataforma: {formatCurrency(currentExpressFinancial.platformRetained)}
                    </span>
                  </div>
                </div>
              </div>

              <DialogFooter className="px-6 py-4 border-t border-border bg-card/95 flex justify-end gap-2.5">
                <Button
                  variant="outline"
                  onClick={() => setIsExpressModalOpen(false)}
                  disabled={updateExpressPricingMutation.isPending}
                  className="rounded-xl font-semibold h-10 px-5 cursor-pointer"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => updateExpressPricingMutation.mutate(expressPriceForm)}
                  isLoading={updateExpressPricingMutation.isPending}
                  className="rounded-xl font-bold h-10 px-6 bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs cursor-pointer"
                >
                  Salvar Alterações
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* =========================================================================
          ABA PAGBANK: REGRAS DE PARCELAMENTO, TAXAS E SIMULADOR INTERATIVO
          ========================================================================= */}
      {activeTab === 'pagbank' && (
        <div className="space-y-6">
          {/* Card 1: Header Executivo & Parâmetros de Parcelamento */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card">
            <CardHeader className="border-b border-border/80 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                      <span>Regras de Parcelamento e Taxas PagBank</span>
                      <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20 uppercase tracking-wide">
                        Cartão de Crédito
                      </span>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Controle a quantidade máxima de parcelas, parcelas sem juros subsidiadas pela plataforma e taxa de juros repassada ao comprador
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => updateInstallmentMutation.mutate(installmentForm)}
                    isLoading={updateInstallmentMutation.isPending}
                    className="rounded-xl font-bold text-xs h-9 px-4 bg-primary text-primary-foreground shadow-xs cursor-pointer"
                  >
                    <Save className="h-4 w-4 mr-1.5" />
                    Salvar Regras de Parcelamento
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-6">
              {installmentLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Bloco 1: Habilitação de Modalidades de Cartão */}
                  <div className="p-4 rounded-2xl bg-muted/40 border border-border space-y-3">
                    <h4 className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <CreditCard className="w-4 h-4 text-primary" />
                      <span>Modalidades de Cartão Habilitadas na Plataforma</span>
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Cartão de Crédito */}
                      <div className="p-3.5 rounded-xl bg-card border border-border flex items-center justify-between">
                        <div>
                          <label className="text-xs font-bold text-foreground block">Cartão de Crédito</label>
                          <p className="text-[11px] text-muted-foreground mt-0.5">Permite pagamentos à vista e parcelados</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={installmentForm.enableCreditCard}
                          onChange={(e) => setInstallmentForm({ ...installmentForm, enableCreditCard: e.target.checked })}
                          className="h-5 w-5 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                        />
                      </div>

                      {/* Cartão de Débito */}
                      <div className="p-3.5 rounded-xl bg-card border border-border flex items-center justify-between">
                        <div>
                          <label className="text-xs font-bold text-foreground block">Cartão de Débito (1x à vista)</label>
                          <p className="text-[11px] text-muted-foreground mt-0.5">Habilita débito online com 3DS</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={installmentForm.enableDebitCard}
                          onChange={(e) => setInstallmentForm({ ...installmentForm, enableDebitCard: e.target.checked })}
                          className="h-5 w-5 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                        />
                      </div>

                      {/* Taxa do Débito */}
                      <div className="p-3.5 rounded-xl bg-card border border-border space-y-1">
                        <label className="text-xs font-bold text-foreground block">Taxa do Débito (% à vista)</label>
                        <Input
                          type="number"
                          step="0.05"
                          min="0"
                          max="15"
                          value={installmentForm.debitRatePercent}
                          onChange={(e) => setInstallmentForm({ ...installmentForm, debitRatePercent: parseFloat(e.target.value) || 0 })}
                          className="rounded-lg h-8 text-xs font-bold"
                        />
                        <p className="text-[10px] text-muted-foreground">Taxa da adquirente para cobrança de débito à vista</p>
                      </div>
                    </div>
                  </div>

                  {/* Bloco 2: Regras Gerais de Parcelamento */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Toggle: Habilitar Parcelamento */}
                    <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-foreground uppercase tracking-wider">
                          Habilitar Parcelamento no Cartão
                        </label>
                        <input
                          type="checkbox"
                          checked={installmentForm.enabled}
                          onChange={(e) => setInstallmentForm({ ...installmentForm, enabled: e.target.checked })}
                          className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Quando ativado, os pacientes podem parcelar consultas no cartão de crédito conforme as regras abaixo.
                      </p>
                    </div>

                    {/* Parcelas Sem Juros */}
                    <div className="p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 space-y-2">
                      <label className="block text-xs font-bold text-emerald-800 dark:text-emerald-400 uppercase tracking-wider">
                        Parcelas Sem Juros (Subsidiadas)
                      </label>
                      <select
                        value={installmentForm.interestFreeInstallments}
                        onChange={(e) => setInstallmentForm({ ...installmentForm, interestFreeInstallments: Number(e.target.value) })}
                        className="w-full px-3 py-2 rounded-xl border border-emerald-500/30 bg-background text-sm font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                      >
                        {[1, 2, 3, 4, 5, 6, 10, 12].map((n) => (
                          <option key={n} value={n}>
                            {n === 1 ? 'Apenas 1x à vista sem juros' : `Até ${n}x sem juros para o paciente`}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        A plataforma absorve o custo financeiro até esse limite de parcelas.
                      </p>
                    </div>

                    {/* Máximo de Parcelas Permitido */}
                    <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-2">
                      <label className="block text-xs font-bold text-foreground uppercase tracking-wider">
                        Máximo de Parcelas Total
                      </label>
                      <select
                        value={installmentForm.maxInstallments}
                        onChange={(e) => setInstallmentForm({ ...installmentForm, maxInstallments: Number(e.target.value) })}
                        className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                      >
                        {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
                          <option key={n} value={n}>
                            Até {n}x no cartão
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Limite máximo exibido no dropdown do checkout.
                      </p>
                    </div>

                    {/* Parcela Mínima */}
                    <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-2">
                      <label className="block text-xs font-bold text-foreground uppercase tracking-wider">
                        Valor Mínimo da Parcela (R$)
                      </label>
                      <Input
                        type="number"
                        step="5"
                        min="10"
                        value={installmentForm.minInstallmentAmount}
                        onChange={(e) => setInstallmentForm({ ...installmentForm, minInstallmentAmount: parseFloat(e.target.value) || 0 })}
                        className="rounded-xl h-10 text-sm font-bold"
                      />
                      <p className="text-xs text-muted-foreground">
                        Evita parcelas irrisórias (ex: consulta de R$ 60 não pode ser dividida em 6x de R$ 10).
                      </p>
                    </div>

                    {/* Valor Mínimo Total para Parcelar */}
                    <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-2">
                      <label className="block text-xs font-bold text-foreground uppercase tracking-wider">
                        Valor Mínimo da Compra para Parcelar (R$)
                      </label>
                      <Input
                        type="number"
                        step="10"
                        min="0"
                        value={installmentForm.minTotalAmountForInstallments}
                        onChange={(e) => setInstallmentForm({ ...installmentForm, minTotalAmountForInstallments: parseFloat(e.target.value) || 0 })}
                        className="rounded-xl h-10 text-sm font-bold"
                      />
                      <p className="text-xs text-muted-foreground">
                        Abaixo deste valor, a consulta só poderá ser paga em 1x à vista.
                      </p>
                    </div>

                    {/* Taxa de Juros Mensal Padrão */}
                    <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-xs font-bold text-foreground uppercase tracking-wider">
                          Taxa Padrão de Juros (% a.m.)
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const rate = installmentForm.monthlyInterestRate;
                            const newRates: Record<number, number> = {};
                            for (let i = 2; i <= 12; i++) {
                              newRates[i] = rate;
                            }
                            setInstallmentForm({ ...installmentForm, ratesByInstallment: newRates });
                          }}
                          className="text-[10px] text-primary hover:underline font-bold cursor-pointer"
                          title="Aplica este percentual a todas as parcelas abaixo"
                        >
                          Aplicar a todas
                        </button>
                      </div>
                      <Input
                        type="number"
                        step="0.05"
                        min="0"
                        max="15"
                        value={installmentForm.monthlyInterestRate}
                        onChange={(e) => setInstallmentForm({ ...installmentForm, monthlyInterestRate: parseFloat(e.target.value) || 0 })}
                        className="rounded-xl h-10 text-sm font-bold"
                      />
                      <p className="text-xs text-muted-foreground">
                        Taxa base mensal aplicada como padrão para as parcelas com juros.
                      </p>
                    </div>

                    {/* Repassar Juros ao Paciente */}
                    <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-2 lg:col-span-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <label className="text-xs font-bold text-foreground uppercase tracking-wider block">
                            Repassar Juros Acima do Limite ao Paciente (Comprador)
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Se ativado, as parcelas excedentes incluirão o acréscimo financeiro no total pago pelo paciente. Se desativado, o valor final pago pelo paciente nunca altera.
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={installmentForm.passInterestToCustomer}
                          onChange={(e) => setInstallmentForm({ ...installmentForm, passInterestToCustomer: e.target.checked })}
                          className="h-5 w-5 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bloco 3: Grade de Taxas de Juros Específicas por Parcela (2x a 12x) */}
                  <div className="p-5 rounded-2xl bg-muted/20 border border-border space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/60 pb-3">
                      <div>
                        <h4 className="text-sm font-bold text-foreground flex items-center gap-1.5">
                          <Scale className="w-4 h-4 text-primary" />
                          <span>Taxa de Juros Customizada por Parcela (2x a 12x)</span>
                        </h4>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Defina a taxa de juros exata (% a.m.) para cada quantidade de parcelas individualmente.
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const newRates: Record<number, number> = {};
                            for (let i = 2; i <= 12; i++) {
                              newRates[i] = installmentForm.monthlyInterestRate;
                            }
                            setInstallmentForm({ ...installmentForm, ratesByInstallment: newRates });
                          }}
                          className="text-xs font-bold px-3 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                        >
                          Uniformizar com Taxa Padrão ({installmentForm.monthlyInterestRate}%)
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => {
                        const isInterestFree = n <= (Number(installmentForm.interestFreeInstallments) || 1);
                        const isAboveMax = n > (Number(installmentForm.maxInstallments) || 12);
                        const currentRate =
                          installmentForm.ratesByInstallment?.[n] !== undefined
                            ? installmentForm.ratesByInstallment[n]
                            : installmentForm.monthlyInterestRate;

                        return (
                          <div
                            key={n}
                            className={cn(
                              'p-3 rounded-xl border flex flex-col justify-between gap-2 transition-all',
                              isAboveMax
                                ? 'opacity-40 bg-muted/20 border-border/40'
                                : isInterestFree
                                ? 'border-emerald-500/30 bg-emerald-500/5'
                                : 'border-border bg-card shadow-xs'
                            )}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-extrabold text-sm text-foreground">{n}x</span>
                              {isInterestFree ? (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
                                  Sem juros
                                </span>
                              ) : (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20">
                                  Com juros
                                </span>
                              )}
                            </div>

                            <div className="space-y-1">
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  step="0.05"
                                  min="0"
                                  max="30"
                                  disabled={isAboveMax}
                                  value={currentRate}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    setInstallmentForm({
                                      ...installmentForm,
                                      ratesByInstallment: {
                                        ...(installmentForm.ratesByInstallment || {}),
                                        [n]: val,
                                      },
                                    });
                                  }}
                                  className="w-full px-2 py-1 rounded-lg border border-input bg-background font-mono font-bold text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                                />
                                <span className="text-[11px] font-bold text-muted-foreground">%</span>
                              </div>
                              <span className="text-[10px] text-muted-foreground block truncate">
                                {isInterestFree ? 'Subsidiado' : 'Repassado'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card 2: Simulador em Tempo Real da Visão do Paciente */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card">
            <CardHeader className="border-b border-border/80 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    <Scale className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground">
                      Simulador em Tempo Real: Visão do Paciente no Checkout
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Digite um valor de consulta para simular exatamente a lista de parcelas e modalidades que o paciente verá na tela
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">Valor Simulado: R$</span>
                  <input
                    type="number"
                    step="10"
                    min="10"
                    value={simulationAmount}
                    onChange={(e) => setSimulationAmount(parseFloat(e.target.value) || 0)}
                    className="w-28 px-3 py-1.5 rounded-xl border border-input bg-background font-mono font-bold text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-4">
              {/* Simulação Débito (se habilitado) */}
              {installmentForm.enableDebitCard && (
                <div className="p-3.5 rounded-2xl border border-blue-500/30 bg-blue-500/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600">
                      <CreditCard className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-extrabold text-foreground text-sm">
                          1x de {formatCurrency(Number(simulationAmount) || 0)} no Débito
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/20">
                          Débito à Vista (3DS)
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Taxa adquirente: {installmentForm.debitRatePercent}% à vista
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-bold font-mono text-muted-foreground">DÉBITO</span>
                </div>
              )}

              {/* Simulação Crédito Parcelado */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {simulatedInstallments.map((opt) => (
                  <div
                    key={opt.numero}
                    className={cn(
                      'p-3.5 rounded-2xl border transition-all flex items-center justify-between gap-3',
                      opt.temJuros
                        ? 'border-border bg-card'
                        : 'border-emerald-500/30 bg-emerald-500/5'
                    )}
                  >
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-extrabold text-foreground text-sm">
                          {opt.numero}x de {formatCurrency(opt.valorParcelaReais)}
                        </span>
                        {!opt.temJuros ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
                            Sem juros
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20">
                            {opt.taxaPercent}% a.m.
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {opt.temJuros ? `Total com juros: ${formatCurrency(opt.valorTotalReais)}` : `Total: ${formatCurrency(opt.valorTotalReais)}`}
                      </p>
                    </div>

                    <span className="text-xs font-bold font-mono text-muted-foreground">
                      #{opt.numero}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* =========================================================================
          ABA PRIORIDADE: ORDEM MANUAL DE MÉDICOS PARCEIROS NA VITRINE & BUSCA
          ========================================================================= */}
      {activeTab === 'priority' && (
        <div className="space-y-6">
          {/* Card 1: Header Executivo & Controle Geral */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card">
            <CardHeader className="border-b border-border/80 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                    <Award className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                      <span>Ordem de Exibição &amp; Prioridade de Médicos Parceiros</span>
                      <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20 uppercase tracking-wide">
                        Vitrine &amp; Home V3
                      </span>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Defina manualmente a sequência exata em que os médicos parceiros aparecem na vitrine da página inicial e no topo das buscas
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => updatePriorityMutation.mutate(doctorPriorityForm)}
                    isLoading={updatePriorityMutation.isPending}
                    className="rounded-xl font-bold text-xs h-9 px-4 bg-primary text-primary-foreground shadow-xs cursor-pointer"
                  >
                    <Save className="h-4 w-4 mr-1.5" />
                    Salvar Ordem da Vitrine
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-4">
              {priorityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Master Toggle */}
                  <div className="p-4 rounded-2xl bg-muted/30 border border-border flex items-center justify-between gap-4">
                    <div>
                      <label className="text-xs font-bold text-foreground uppercase tracking-wider block">
                        Ativar Ordenação Manual de Médicos Parceiros
                      </label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Quando ativo, os médicos da lista abaixo aparecerão em primeiro lugar na vitrine e nos resultados de busca, respeitando a ordem configurada.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={doctorPriorityForm.enabled}
                      onChange={(e) => setDoctorPriorityForm({ ...doctorPriorityForm, enabled: e.target.checked })}
                      className="h-5 w-5 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                    />
                  </div>

                  {/* Adicionar Médico à Lista */}
                  <div className="p-4 rounded-2xl bg-primary/5 border border-primary/20 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <Plus className="w-4 h-4 text-primary" />
                        <span>Adicionar Médico à Lista de Prioridade</span>
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {doctorPriorityForm.priorityList.length} médico(s) configurado(s) na vitrine
                      </span>
                    </div>

                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Buscar médico aprovado por nome, CRM ou especialidade para adicionar..."
                        value={doctorSearchToAdd}
                        onChange={(e) => setDoctorSearchToAdd(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 rounded-xl border border-input bg-background text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    {/* Resultados da Busca para Adição */}
                    {doctorSearchToAdd.trim() && (
                      <div className="max-h-52 overflow-y-auto rounded-xl border border-border bg-card divide-y divide-border/60">
                        {((priorityData?.doctors || []) as any[])
                          .filter((doc) => {
                            const alreadyIn = doctorPriorityForm.priorityList.some((item) => item.doctorId === doc.id);
                            if (alreadyIn) return false;
                            const s = doctorSearchToAdd.toLowerCase();
                            return (
                              doc.name.toLowerCase().includes(s) ||
                              doc.crm.includes(s) ||
                              doc.specialty.toLowerCase().includes(s)
                            );
                          })
                          .slice(0, 10)
                          .map((doc) => (
                            <div key={doc.id} className="p-2.5 flex items-center justify-between hover:bg-muted/40 transition-colors">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary font-bold flex items-center justify-center text-xs shrink-0 overflow-hidden">
                                  {doc.photoUrl ? (
                                    <img src={doc.photoUrl} alt={doc.name} className="w-full h-full object-cover" />
                                  ) : (
                                    doc.name.charAt(0)
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-bold text-foreground truncate">{doc.name}</p>
                                  <p className="text-[11px] text-muted-foreground truncate">
                                    {doc.specialty} • CRM {doc.crm}/{doc.crmState}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                type="button"
                                onClick={() => {
                                  addDoctorToPriority(doc.id);
                                  setDoctorSearchToAdd('');
                                }}
                                className="h-7 text-[11px] font-bold rounded-lg px-2.5 gap-1 shrink-0"
                              >
                                <Plus className="w-3 h-3" />
                                <span>Adicionar</span>
                              </Button>
                            </div>
                          ))}
                        {((priorityData?.doctors || []) as any[]).filter((doc) => {
                          const alreadyIn = doctorPriorityForm.priorityList.some((item) => item.doctorId === doc.id);
                          if (alreadyIn) return false;
                          const s = doctorSearchToAdd.toLowerCase();
                          return (
                            doc.name.toLowerCase().includes(s) ||
                            doc.crm.includes(s) ||
                            doc.specialty.toLowerCase().includes(s)
                          );
                        }).length === 0 && (
                          <div className="p-4 text-center text-xs text-muted-foreground">
                            Nenhum médico aprovado encontrado com esse critério.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Lista Reordenável de Médicos Parceiros Priorizados */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold text-foreground uppercase tracking-wider">
                        Médicos Parceiros Ordenados na Vitrine
                      </h4>
                      <span className="text-[11px] text-muted-foreground">
                        Use as setas para alterar a posição (#1 aparece primeiro na tela inicial)
                      </span>
                    </div>

                    {doctorPriorityForm.priorityList.length === 0 ? (
                      <div className="p-8 text-center border border-dashed border-border rounded-2xl space-y-2">
                        <Award className="w-8 h-8 text-muted-foreground/50 mx-auto" />
                        <p className="text-xs font-bold text-foreground">Nenhum médico parceiro adicionado à vitrine</p>
                        <p className="text-[11px] text-muted-foreground max-w-sm mx-auto">
                          Busque e selecione médicos aprovados acima para colocá-los na ordem de prioridade oficial da Medikei.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        {doctorPriorityForm.priorityList.map((item, idx) => {
                          const docInfo = ((priorityData?.doctors || []) as any[]).find((d) => d.id === item.doctorId);
                          return (
                            <div
                              key={item.doctorId}
                              className={cn(
                                'p-3.5 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3',
                                item.isPinned
                                  ? 'border-amber-500/40 bg-amber-500/5 shadow-xs'
                                  : 'border-border bg-card shadow-xs'
                              )}
                            >
                              {/* Posicionamento & Informações do Médico */}
                              <div className="flex items-center gap-3 min-w-0">
                                {/* Posição / Rank Badge */}
                                <div className="flex items-center gap-1 shrink-0">
                                  <span className="w-7 h-7 rounded-xl bg-muted font-mono font-black text-xs text-foreground flex items-center justify-center border border-border">
                                    #{idx + 1}
                                  </span>

                                  {/* Botões Mover para Cima / Baixo */}
                                  <div className="flex flex-col gap-0.5">
                                    <button
                                      type="button"
                                      disabled={idx === 0}
                                      onClick={() => moveDoctorPriority(idx, 'up')}
                                      className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-20 cursor-pointer transition-colors"
                                      title="Mover para cima"
                                    >
                                      <ArrowUp className="w-3 h-3" />
                                    </button>
                                    <button
                                      type="button"
                                      disabled={idx === doctorPriorityForm.priorityList.length - 1}
                                      onClick={() => moveDoctorPriority(idx, 'down')}
                                      className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-20 cursor-pointer transition-colors"
                                      title="Mover para baixo"
                                    >
                                      <ArrowDown className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>

                                {/* Avatar & Dados */}
                                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary font-bold flex items-center justify-center text-sm shrink-0 overflow-hidden border border-border">
                                  {docInfo?.photoUrl ? (
                                    <img src={docInfo.photoUrl} alt={docInfo.name} className="w-full h-full object-cover" />
                                  ) : (
                                    docInfo?.name?.charAt(0) || 'D'
                                  )}
                                </div>

                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-bold text-xs sm:text-sm text-foreground truncate">
                                      {docInfo?.name || item.doctorId}
                                    </span>
                                    {item.isPinned && (
                                      <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/25 flex items-center gap-0.5">
                                        <Pin className="w-2.5 h-2.5" />
                                        <span>Fixado no Topo</span>
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[11px] text-muted-foreground truncate">
                                    {docInfo?.specialty || 'Especialista'} • CRM {docInfo?.crm}/{docInfo?.crmState || 'SP'} • ★ {docInfo?.rating ? Number(docInfo.rating).toFixed(1) : '5.0'}
                                  </p>
                                </div>
                              </div>

                              {/* Configuração de Badge & Ações */}
                              <div className="flex items-center gap-2.5 self-end sm:self-auto shrink-0 flex-wrap">
                                {/* Selo Customizado / Presets */}
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[11px] font-semibold text-muted-foreground">Selo:</span>
                                  <input
                                    type="text"
                                    placeholder="Ex: Mais Escolhida"
                                    value={item.badge || ''}
                                    onChange={(e) => updateDoctorBadge(item.doctorId, e.target.value)}
                                    className="w-32 sm:w-36 px-2.5 py-1 text-xs rounded-lg border border-input bg-background font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                  />
                                </div>

                                {/* Toggle Pin no Topo */}
                                <button
                                  type="button"
                                  onClick={() => togglePinDoctor(item.doctorId)}
                                  className={cn(
                                    'p-1.5 rounded-lg border transition-colors cursor-pointer',
                                    item.isPinned
                                      ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30'
                                      : 'bg-muted/40 text-muted-foreground border-border hover:text-foreground'
                                  )}
                                  title={item.isPinned ? 'Desafixar do topo' : 'Fixar no topo absoluto'}
                                >
                                  <Pin className="w-3.5 h-3.5" />
                                </button>

                                {/* Remover */}
                                <button
                                  type="button"
                                  onClick={() => removeDoctorFromPriority(item.doctorId)}
                                  className="p-1.5 rounded-lg border border-border bg-muted/40 text-muted-foreground hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:border-rose-300 transition-colors cursor-pointer"
                                  title="Remover da lista de prioridade"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card 4: Pré-Visualização da Vitrine em Tempo Real */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card">
            <CardHeader className="border-b border-border/80 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                  <Star className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-foreground">
                    Pré-Visualização em Tempo Real: Vitrine da Página Inicial
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Veja exatamente a ordem e os selos que os pacientes verão na seção "Especialistas Disponíveis para Agendamento"
                  </p>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6">
              {doctorPriorityForm.priorityList.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground">
                  Adicione médicos parceiros acima para visualizar a prévia da vitrine.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {doctorPriorityForm.priorityList.map((item, idx) => {
                    const docInfo = ((priorityData?.doctors || []) as any[]).find((d) => d.id === item.doctorId);
                    return (
                      <div
                        key={item.doctorId}
                        className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-card shadow-xs space-y-3 relative overflow-hidden"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono font-extrabold text-[11px] text-muted-foreground">
                            Posição #{idx + 1}
                          </span>
                          {item.badge && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 flex items-center gap-1">
                              <Sparkles className="w-2.5 h-2.5" />
                              <span>{item.badge}</span>
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary font-bold flex items-center justify-center text-base shrink-0 overflow-hidden border border-border">
                            {docInfo?.photoUrl ? (
                              <img src={docInfo.photoUrl} alt={docInfo.name} className="w-full h-full object-cover" />
                            ) : (
                              docInfo?.name?.charAt(0) || 'D'
                            )}
                          </div>
                          <div className="min-w-0">
                            <h5 className="font-bold text-sm text-foreground truncate">{docInfo?.name || 'Dr. Médico Parceiro'}</h5>
                            <p className="text-xs font-semibold text-primary truncate">{docInfo?.specialty || 'Clínica Geral'}</p>
                            <p className="text-[11px] text-muted-foreground">CRM {docInfo?.crm}/{docInfo?.crmState || 'SP'}</p>
                          </div>
                        </div>

                        <div className="pt-2 border-t border-border/60 flex items-center justify-between text-xs">
                          <span className="font-bold text-foreground">★ {docInfo?.rating ? Number(docInfo.rating).toFixed(1) : '5.0'}</span>
                          <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">Agenda Aberta</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'cancellation' && (
        <div className="space-y-6">
          {/* Card 1: Header Executivo & Parâmetros Operacionais */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card">
            <CardHeader className="border-b border-border/80 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20">
                    <RotateCcw className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                      <span>Políticas de Cancelamento &amp; Estorno PagBank</span>
                      <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20 uppercase tracking-wide">
                        CDC Art. 49 &amp; CFM
                      </span>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Regras de antecedência sem ônus, penalidades médicas e processamento automático de estorno via PagBank
                    </p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              <ConfigItem
                config={getConfig('CANCEL_WINDOW_HOURS')}
                label="Janela de Cancelamento sem Ônus (Paciente)"
                description="Horas de antecedência que o paciente pode cancelar com estorno integral de 100%"
                editingKey={editingKey}
                editValue={editValue}
                onEdit={handleEditConfig}
                onSave={handleSaveConfig}
                onCancel={() => setEditingKey(null)}
                onEditChange={setEditValue}
                formatFn={(v) => `${v?.hours || v} horas antes da consulta`}
              />

              <ConfigItem
                config={getConfig('CANCEL_FEE_PERCENT')}
                label="Taxa de Cancelamento Tardio (Médico)"
                description="Percentual debitado do médico caso cancele o atendimento sem justificativa dentro da janela"
                editingKey={editingKey}
                editValue={editValue}
                onEdit={handleEditConfig}
                onSave={handleSaveConfig}
                onCancel={() => setEditingKey(null)}
                onEditChange={setEditValue}
                formatFn={formatPercent}
              />

              <ConfigItem
                config={getConfig('EXPRESSA_TIMEOUT_MINUTES')}
                label="Timeout de Espera na Fila Expressa (Plantão 24h)"
                description="Minutos máximos que o paciente aguarda na fila antes do sistema estornar 100% automaticamente"
                editingKey={editingKey}
                editValue={editValue}
                onEdit={handleEditConfig}
                onSave={handleSaveConfig}
                onCancel={() => setEditingKey(null)}
                onEditChange={setEditValue}
                formatFn={(v) => `${v?.minutes || v} minutos`}
              />
            </CardContent>
          </Card>

          {/* Card 2: Matriz Visual de Escalonamento de Reembolso */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 text-left">
              <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 block uppercase">
                &gt; 24h de Antecedência
              </span>
              <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">100% Estorno</p>
              <span className="text-xs text-muted-foreground mt-0.5 block leading-relaxed">
                Reembolso integral e automático. O paciente cancela sem cobrança de taxas.
              </span>
            </div>

            <div className="p-4 rounded-2xl border border-blue-500/20 bg-blue-500/5 text-left">
              <span className="text-[11px] font-bold text-blue-700 dark:text-blue-400 block uppercase">
                12h a 24h de Antecedência
              </span>
              <p className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">80% Estorno</p>
              <span className="text-xs text-muted-foreground mt-0.5 block leading-relaxed">
                Estorno de 80% do valor pago ou opção de reagendamento sem custo adicional.
              </span>
            </div>

            <div className="p-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 text-left">
              <span className="text-[11px] font-bold text-amber-700 dark:text-amber-400 block uppercase">
                2h a 12h de Antecedência
              </span>
              <p className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">50% Estorno</p>
              <span className="text-xs text-muted-foreground mt-0.5 block leading-relaxed">
                Estorno de 50% retendo compensação de reserva do horário médico.
              </span>
            </div>

            <div className="p-4 rounded-2xl border border-rose-500/20 bg-rose-500/5 text-left">
              <span className="text-[11px] font-bold text-rose-700 dark:text-rose-400 block uppercase">
                &lt; 2h (No-Show Iminente)
              </span>
              <p className="text-2xl font-black text-rose-600 dark:text-rose-400 mt-1">0% Estorno</p>
              <span className="text-xs text-muted-foreground mt-0.5 block leading-relaxed">
                Horário bloqueado dedicado. Sem estorno de valor (apenas reagendamento sujeito à vaga).
              </span>
            </div>
          </div>

          {/* Card 3: Regras Regulatórias & Gateway PagBank */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-3xl border border-border shadow-xs bg-card p-6 space-y-3">
              <div className="flex items-center gap-2.5 text-primary">
                <Scale className="w-5 h-5" />
                <h3 className="font-bold text-sm sm:text-base text-foreground">Conformidade Legal &amp; Resoluções CFM</h3>
              </div>
              <ul className="text-xs text-muted-foreground space-y-2 leading-relaxed">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>
                    <strong>Direito de Arrependimento (CDC Art. 49):</strong> O consumidor tem até 7 dias corridos após a contratação do serviço online para desistência com devolução de 100% do valor.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>
                    <strong>Cancelamento por iniciativa do Médico:</strong> O paciente tem direito garantido a 100% de estorno imediato no PagBank, independentemente do horário em que o médico cancelar.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>
                    <strong>Plantão Express 24h:</strong> Caso o paciente desista antes de o médico aceitar ou se o timeout for atingido, o estorno de 100% é processado de forma automática e instantânea.
                  </span>
                </li>
              </ul>
            </Card>

            <Card className="rounded-3xl border border-border shadow-xs bg-card p-6 space-y-3">
              <div className="flex items-center gap-2.5 text-blue-600">
                <CreditCard className="w-5 h-5" />
                <h3 className="font-bold text-sm sm:text-base text-foreground">Processamento do Estorno no PagBank</h3>
              </div>
              <div className="text-xs text-muted-foreground space-y-2.5 leading-relaxed">
                <div className="p-3 rounded-xl bg-muted/40 border border-border">
                  <span className="font-bold text-foreground block">⚡ Estorno via PIX (Instantâneo)</span>
                  <span>
                    Aciona o endpoint oficial <code className="text-primary font-mono text-[11px]">POST /charges/:id/cancel</code>. O valor é devolvido diretamente à conta bancária pagadora em poucos segundos.
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-muted/40 border border-border">
                  <span className="font-bold text-foreground block">💳 Estorno via Cartão de Crédito</span>
                  <span>
                    A cobrança é cancelada junto à bandeira. O valor é creditado na fatura do titular (dependendo do ciclo de fechamento, na fatura atual ou na subsequente).
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Card 4: Gestão de Solicitações de Cancelamento (cancellation-requests) */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card overflow-hidden">
            <CardHeader className="border-b border-border/80 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    <span>Auditoria de Solicitações de Cancelamento</span>
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Pedidos de cancelamento e estorno abertos por pacientes ou médicos
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {cancelRequestsLoading ? (
                <div className="py-12 text-center text-xs text-muted-foreground">
                  Carregando solicitações...
                </div>
              ) : (!cancelRequestsData?.data || cancelRequestsData.data.length === 0) ? (
                <div className="py-12 text-center text-xs text-muted-foreground">
                  Nenhuma solicitação de cancelamento registrada no sistema.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted/40 border-b border-border font-bold text-muted-foreground uppercase tracking-wider">
                      <tr>
                        <th className="px-5 py-3.5">Data / Hora</th>
                        <th className="px-5 py-3.5">Solicitante</th>
                        <th className="px-5 py-3.5">Motivo Informado</th>
                        <th className="px-5 py-3.5 text-right">Valor Estorno</th>
                        <th className="px-5 py-3.5 text-center">Status</th>
                        <th className="px-5 py-3.5 text-right">Ação Administrativa</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {cancelRequestsData.data.map((req: any) => {
                        const isPending = req.status === 'PENDING';
                        const isApproved = req.status === 'APPROVED';
                        return (
                          <tr key={req.id} className="hover:bg-muted/30 transition-colors">
                            <td className="px-5 py-3.5 whitespace-nowrap text-muted-foreground font-mono">
                              {new Date(req.createdAt).toLocaleDateString('pt-BR')} {new Date(req.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-5 py-3.5 font-bold text-foreground">
                              {req.requestedBy === 'PATIENT' ? '👤 Paciente' : req.requestedBy === 'DOCTOR' ? '🩺 Médico' : req.requestedBy}
                            </td>
                            <td className="px-5 py-3.5 text-muted-foreground max-w-xs truncate" title={req.reason}>
                              {req.reason || 'Sem justificativa'}
                            </td>
                            <td className="px-5 py-3.5 text-right font-bold text-foreground whitespace-nowrap">
                              {req.refundAmount ? formatCurrency(Number(req.refundAmount)) : 'A calcular'}
                            </td>
                            <td className="px-5 py-3.5 text-center whitespace-nowrap">
                              <span
                                className={cn(
                                  'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold',
                                  isApproved
                                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20'
                                    : isPending
                                    ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20'
                                    : 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20'
                                )}
                              >
                                {isApproved && <CheckCircle2 className="w-3 h-3" />}
                                {isPending && <Clock className="w-3 h-3" />}
                                <span>{isApproved ? 'Aprovado (Estornado)' : isPending ? 'Pendente' : 'Rejeitado'}</span>
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-right whitespace-nowrap">
                              {isPending ? (
                                <div className="flex items-center justify-end gap-1.5">
                                  <Button
                                    size="sm"
                                    onClick={() => setApproveConfirmId(req.id)}
                                    className="h-8 text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-2.5"
                                  >
                                    Aprovar &amp; Estornar no PagBank
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setRejectModalId(req.id);
                                      setRejectReason('');
                                    }}
                                    className="h-8 text-[11px] font-semibold text-destructive hover:bg-destructive/10 rounded-lg px-2.5"
                                  >
                                    Rejeitar
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-[11px] text-muted-foreground">
                                  {req.adminNote ? `Nota: ${req.adminNote}` : 'Processado'}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Modal Confirmar Aprovação de Cancelamento com Estorno PagBank */}
          {approveConfirmId && (
            <Dialog open={true} onOpenChange={(open) => !open && setApproveConfirmId(null)}>
              <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border border-border shadow-2xl bg-card">
                <DialogHeader className="px-6 py-5 border-b border-border bg-card">
                  <DialogTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                    <RotateCcw className="w-5 h-5 text-emerald-600" />
                    <span>Aprovar Cancelamento &amp; Estorno</span>
                  </DialogTitle>
                  <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                    O cancelamento liberará o horário e enviará o estorno para o PagBank
                  </DialogDescription>
                </DialogHeader>

                <div className="p-6 space-y-3">
                  <p className="text-sm text-foreground">
                    Deseja aprovar esta solicitação de cancelamento e emitir a ordem de estorno imediata na API do <strong>PagBank</strong>?
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Se a consulta tiver sido paga via PIX, o estorno cairá de volta diretamente na conta bancária do paciente. Se tiver sido paga via Cartão, o crédito será estornado na fatura.
                  </p>
                </div>

                <DialogFooter className="px-6 py-4 border-t border-border bg-card flex justify-end gap-2.5">
                  <Button
                    variant="outline"
                    onClick={() => setApproveConfirmId(null)}
                    disabled={approveCancelMutation.isPending}
                    className="rounded-xl font-semibold h-10 px-4 cursor-pointer"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => approveCancelMutation.mutate({ id: approveConfirmId })}
                    isLoading={approveCancelMutation.isPending}
                    className="rounded-xl font-bold h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs cursor-pointer"
                  >
                    Confirmar Estorno PagBank
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Modal Rejeitar Solicitação de Cancelamento */}
          {rejectModalId && (
            <Dialog open={true} onOpenChange={(open) => !open && setRejectModalId(null)}>
              <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border border-border shadow-2xl bg-card">
                <DialogHeader className="px-6 py-5 border-b border-border bg-card">
                  <DialogTitle className="text-lg font-bold text-destructive flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                    <span>Rejeitar Solicitação de Cancelamento</span>
                  </DialogTitle>
                  <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                    Informe o motivo administrativo para a recusa
                  </DialogDescription>
                </DialogHeader>

                <div className="p-6 space-y-3">
                  <label className="text-xs font-bold text-foreground block">
                    Motivo da Recusa <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="text"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Ex: Cancelamento fora do prazo regulamentar sem justificativa clínica..."
                    className="rounded-xl h-11 text-xs"
                  />
                </div>

                <DialogFooter className="px-6 py-4 border-t border-border bg-card flex justify-end gap-2.5">
                  <Button
                    variant="outline"
                    onClick={() => setRejectModalId(null)}
                    disabled={rejectCancelMutation.isPending}
                    className="rounded-xl font-semibold h-10 px-4 cursor-pointer"
                  >
                    Voltar
                  </Button>
                  <Button
                    onClick={() => rejectCancelMutation.mutate({ id: rejectModalId, adminNote: rejectReason })}
                    disabled={!rejectReason.trim() || rejectCancelMutation.isPending}
                    isLoading={rejectCancelMutation.isPending}
                    className="rounded-xl font-bold h-10 px-5 bg-destructive hover:bg-destructive/90 text-white shadow-xs cursor-pointer"
                  >
                    Rejeitar Solicitação
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}

      {activeTab === 'consultation' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Durações de Consulta
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <ConfigItem
                config={getConfig('EXPRESSA_DURATION_MINUTES')}
                label="Duração Consulta Expressa"
                description="Duração máxima da consulta expressa em minutos"
                editingKey={editingKey}
                editValue={editValue}
                onEdit={handleEditConfig}
                onSave={handleSaveConfig}
                onCancel={() => setEditingKey(null)}
                onEditChange={setEditValue}
                formatFn={(v) => `${v?.minutes || v} minutos`}
              />

              <ConfigItem
                config={getConfig('SCHEDULED_DEFAULT_DURATION')}
                label="Duração Padrão Agendada"
                description="Duração padrão da consulta agendada em minutos"
                editingKey={editingKey}
                editValue={editValue}
                onEdit={handleEditConfig}
                onSave={handleSaveConfig}
                onCancel={() => setEditingKey(null)}
                onEditChange={setEditValue}
                formatFn={(v) => `${v?.minutes || v} minutos`}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Fila Expressa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <ConfigItem
                config={getConfig('EXPRESSA_TIMEOUT_MINUTES')}
                label="Timeout Fila Expressa"
                description="Minutos que o paciente aguarda na fila antes de pedir estorno"
                editingKey={editingKey}
                editValue={editValue}
                onEdit={handleEditConfig}
                onSave={handleSaveConfig}
                onCancel={() => setEditingKey(null)}
                onEditChange={setEditValue}
                formatFn={(v) => `${v?.minutes || v} minutos`}
              />

              <ConfigItem
                config={getConfig('EXPRESSA_EXTEND_MINUTES')}
                label="Extensão Fila Expressa"
                description="Minutos adicionais quando paciente opta por aguardar mais"
                editingKey={editingKey}
                editValue={editValue}
                onEdit={handleEditConfig}
                onSave={handleSaveConfig}
                onCancel={() => setEditingKey(null)}
                onEditChange={setEditValue}
                formatFn={(v) => `${v?.minutes || v} minutos`}
              />

              <ConfigItem
                config={getConfig('DOCTOR_INVITATION_TIMEOUT_SECONDS')}
                label="Timeout Convite Médico"
                description="Segundos que o médico tem para aceitar/rejeitar convite"
                editingKey={editingKey}
                editValue={editValue}
                onEdit={handleEditConfig}
                onSave={handleSaveConfig}
                onCancel={() => setEditingKey(null)}
                onEditChange={setEditValue}
                formatFn={(v) => `${v?.seconds || v} segundos`}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* CRM VALIDATION & KEY ROTATION TAB */}
      {activeTab === 'crm' && (
        <div className="space-y-6">
          {/* Status & Automated Verification Switch */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-primary" />
                    Validação Automática de CRM
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Consulta a base oficial do Conselho Federal de Medicina (CFM) via API Consulta CRM durante o cadastro de médicos.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                    crmConfig.enabled
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {crmConfig.enabled ? '● Ativo em Produção' : '○ Validação Desativada'}
                  </span>

                  <button
                    type="button"
                    onClick={() => toggleCrmMutation.mutate(!crmConfig.enabled)}
                    disabled={toggleCrmMutation.isPending || crmLoading}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      crmConfig.enabled
                        ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                        : 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                    }`}
                  >
                    {crmConfig.enabled ? 'Desativar' : 'Ativar Validação'}
                  </button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Key Pool Management */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5 text-blue-600" />
                    Pool de Chaves da API (Alternância Automática)
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Cadastre múltiplas chaves. O sistema alterna automaticamente entre elas (Round-Robin) para distribuir o limite de consultas.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setIsAddingKey(!isAddingKey)}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  Nova Chave
                </button>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Form to add a new key */}
              {isAddingKey && (
                <div className="p-4 rounded-xl border border-blue-200 bg-blue-50/50 space-y-3 animate-in fade-in duration-200">
                  <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <Key className="h-4 w-4 text-blue-600" />
                    Cadastrar Nova Chave de API
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Nome / Identificador da Chave
                      </label>
                      <input
                        type="text"
                        placeholder="Ex.: Chave Secundária (Patrick)"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Chave da API (Token Numérico)
                      </label>
                      <input
                        type="text"
                        placeholder="Ex.: 8750066851"
                        value={newKeyValue}
                        onChange={(e) => setNewKeyValue(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Cota Mensal de Consultas
                      </label>
                      <input
                        type="number"
                        placeholder="100"
                        min="1"
                        value={newKeyQuota}
                        onChange={(e) => setNewKeyQuota(parseInt(e.target.value, 10) || 100)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setIsAddingKey(false)}
                      className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      Cancelar
                    </button>

                    <button
                      type="button"
                      disabled={!newKeyValue.trim() || addCrmKeyMutation.isPending}
                      onClick={() => {
                        addCrmKeyMutation.mutate({
                          name: newKeyName.trim() || 'Nova Chave',
                          key: newKeyValue.trim(),
                          quota: newKeyQuota,
                        });
                      }}
                      className="px-4 py-1.5 text-xs bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {addCrmKeyMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      Salvar Chave no Pool
                    </button>
                  </div>
                </div>
              )}

              {/* Keys list */}
              {crmLoading ? (
                <p className="text-sm text-gray-500 py-4 text-center">Carregando chaves da API...</p>
              ) : crmConfig.keys.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">Nenhuma chave de API cadastrada ainda.</p>
              ) : (
                <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
                  {crmConfig.keys.map((k) => (
                    <div
                      key={k.id}
                      className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white hover:bg-gray-50/70 transition-colors"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-gray-900">{k.name}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            k.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {k.isActive ? 'Ativa' : 'Pausada'}
                          </span>
                        </div>

                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>Token: <strong className="font-mono text-gray-700">{k.maskedKey}</strong></span>
                          <span>Uso: <strong className="text-gray-900">{k.usage || 0} / {k.quota || 100}</strong> consultas</span>
                          {k.lastUsedAt && (
                            <span>Último uso: {new Date(k.lastUsedAt).toLocaleDateString('pt-BR')} às {new Date(k.lastUsedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                          )}
                        </div>

                        {k.lastError && (
                          <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span>Erro recente: {k.lastError}</span>
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Toggle active */}
                        <button
                          type="button"
                          onClick={() => updateCrmKeyMutation.mutate({ id: k.id, isActive: !k.isActive })}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            k.isActive
                              ? 'text-gray-600 hover:bg-gray-100 border border-gray-200'
                              : 'text-green-700 bg-green-50 hover:bg-green-100 border border-green-200'
                          }`}
                        >
                          {k.isActive ? 'Pausar' : 'Ativar'}
                        </button>

                        {/* Test this key */}
                        <button
                          type="button"
                          onClick={() => {
                            setTestSelectedKeyId(k.id);
                            handleRunCrmTest();
                          }}
                          className="px-3 py-1.5 text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg border border-blue-200 flex items-center gap-1"
                        >
                          <Play className="w-3 h-3" />
                          Testar
                        </button>

                        {/* Delete key */}
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Tem certeza de que deseja remover a chave "${k.name}"?`)) {
                              deleteCrmKeyMutation.mutate(k.id);
                            }
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir Chave"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Real-time Interactive Test Sandbox */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Play className="h-5 w-5 text-emerald-600" />
                Testar Consulta de CRM em Tempo Real
              </CardTitle>
              <p className="text-sm text-gray-500">
                Execute uma consulta de teste ao vivo contra a API Consulta CRM para validar a resposta do servidor e o status do médico no CFM.
              </p>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Número do CRM
                  </label>
                  <input
                    type="text"
                    value={testCrm}
                    onChange={(e) => setTestCrm(e.target.value)}
                    placeholder="Ex.: 123456"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Estado (UF)
                  </label>
                  <input
                    type="text"
                    maxLength={2}
                    value={testUf}
                    onChange={(e) => setTestUf(e.target.value.toUpperCase())}
                    placeholder="SP"
                    className="w-full px-3 py-2 text-sm uppercase text-center font-bold border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Chave a Utilizar
                  </label>
                  <select
                    value={testSelectedKeyId}
                    onChange={(e) => setTestSelectedKeyId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  >
                    <option value="auto">⚡ Pool Automático (Round-Robin)</option>
                    {crmConfig.keys.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.name} ({k.maskedKey})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={handleRunCrmTest}
                    disabled={testRunning || !testCrm || !testUf}
                    className="w-full h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg text-sm transition-colors flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
                  >
                    {testRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    <span>Executar Teste</span>
                  </button>
                </div>
              </div>

              {/* Test Error Message */}
              {testError && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold">Falha no teste de conexão</p>
                    <p className="text-xs mt-0.5">{testError}</p>
                  </div>
                </div>
              )}

              {/* Test Result Display */}
              {testResult && (
                <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-3 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between border-b border-emerald-200/80 pb-2.5">
                    <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <span>Resposta da Consulta CRM Recebida com Sucesso!</span>
                    </div>

                    {testResult.keyUsedName && (
                      <span className="text-xs bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-full font-medium">
                        Chave: {testResult.keyUsedName}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div className="p-3 bg-white rounded-lg border border-emerald-100">
                      <span className="text-gray-500 font-medium block">Nome do Médico (CFM)</span>
                      <strong className="text-gray-900 text-sm">{testResult.doctorName || 'Não localizado'}</strong>
                    </div>

                    <div className="p-3 bg-white rounded-lg border border-emerald-100">
                      <span className="text-gray-500 font-medium block">CRM / UF</span>
                      <strong className="text-gray-900 text-sm">{testResult.crm || testCrm} / {testResult.crmState || testUf}</strong>
                    </div>

                    <div className="p-3 bg-white rounded-lg border border-emerald-100">
                      <span className="text-gray-500 font-medium block">Situação Cadastral</span>
                      <strong className={`text-sm ${testResult.isValid ? 'text-green-700' : 'text-red-700'}`}>
                        {testResult.situation || (testResult.isValid ? 'Ativo' : 'Inativo')}
                      </strong>
                    </div>
                  </div>

                  {testResult.rawResponse && (
                    <details className="text-xs text-gray-600">
                      <summary className="cursor-pointer font-semibold text-gray-700 hover:text-emerald-700">
                        Visualizar JSON bruto retornado pela API
                      </summary>
                      <pre className="mt-2 p-3 bg-gray-900 text-gray-100 rounded-lg font-mono text-[11px] overflow-x-auto">
                        {JSON.stringify(testResult.rawResponse, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============================================================== */}
      {/* ABA: WHATSAPP & CHATWOOT (MENSAGERIA MÉDICA E SUPORTE UNIFICADO)*/}
      {/* ============================================================== */}
      {activeTab === 'whatsapp' && (
        <div className="space-y-6 animate-in fade-in duration-200">
          {/* Header com Visão Geral e Botão Salvar */}
          <div className="rounded-3xl border border-border bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent p-6 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start sm:items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/30">
                <MessageSquare className="w-6 h-6" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-black text-foreground tracking-tight">
                    Módulo WhatsApp Cloud API &amp; Chatwoot
                  </h2>
                  <span className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full border ${
                    chatwootForm.isEnabled
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-300'
                      : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400 border-neutral-300'
                  }`}>
                    {chatwootForm.isEnabled ? '● Módulo Ativo' : '○ Módulo Desativado'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                  Envio transparente de receitas, atestados e exames diretamente da teleconsulta para o WhatsApp do paciente, com helpdesk humanizado de atendentes em número único homologado.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5 self-start md:self-auto shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleTestChatwootConnection}
                disabled={isTestingChatwootConnection}
                className="rounded-xl font-semibold text-xs h-10 gap-1.5 cursor-pointer"
              >
                {isTestingChatwootConnection ? (
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                ) : (
                  <Activity className="w-4 h-4 text-emerald-600" />
                )}
                <span>Testar Conexão</span>
              </Button>

              <Button
                size="sm"
                onClick={() => updateChatwootMutation.mutate(chatwootForm)}
                disabled={updateChatwootMutation.isPending}
                className="rounded-xl font-bold text-xs h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm gap-1.5 cursor-pointer"
              >
                {updateChatwootMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>Salvar Configurações</span>
              </Button>
            </div>
          </div>

          {/* Card de Arquitetura & Dois Pilares */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card overflow-hidden">
            <CardHeader className="border-b border-border/70 pb-3 bg-muted/20">
              <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <span>Topologia &amp; Pilares de Operação do Sistema</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-blue-500/5 border border-blue-500/20 space-y-2">
                  <div className="flex items-center gap-2 font-bold text-xs text-blue-700 dark:text-blue-400">
                    <span className="w-2 h-2 rounded-full bg-blue-600" />
                    <span>Frente 1: Atendimento Humano &amp; Helpdesk (Síncrono)</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Operadores, secretárias e suporte acessam o painel multiagente do Chatwoot (<strong className="text-foreground">chat.medikei.com.br</strong>) para responder dúvidas, reagendar consultas e realizar triagem humanizada quando o paciente envia mensagens pelo WhatsApp.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 space-y-2">
                  <div className="flex items-center gap-2 font-bold text-xs text-emerald-700 dark:text-emerald-400">
                    <span className="w-2 h-2 rounded-full bg-emerald-600" />
                    <span>Frente 2: Disparo Headless de Documentos Clínicos (Assíncrono)</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Médicos na teleconsulta e administradores enviam Receitas Digitais ICP-Brasil, Atestados e Pedidos de Exames com 1 clique direto no chat da chamada. O backend Medikei aciona a API do Chatwoot de forma 100% transparente.
                  </p>
                </div>
              </div>

              {/* Status do Teste de Conexão */}
              {chatwootConnectionStatus && (
                <div className={`p-3.5 rounded-xl text-xs font-semibold flex items-center gap-2 border ${
                  chatwootConnectionStatus.success
                    ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200'
                    : 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300 border-red-200'
                }`}>
                  {chatwootConnectionStatus.success ? (
                    <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                  )}
                  <span>{chatwootConnectionStatus.message}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Grid de Configurações: Chatwoot e Meta WhatsApp API */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* PAINEL 1: Conexão Chatwoot */}
            <Card className="rounded-3xl border border-border shadow-xs bg-card">
              <CardHeader className="border-b border-border/70 pb-3">
                <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-primary" />
                  <span>1. Conexão com o Chatwoot (Self-Hosted)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-5 space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">URL da Instância Chatwoot</label>
                  <input
                    type="text"
                    placeholder="https://chat.medikei.com.br"
                    value={chatwootForm.frontendUrl}
                    onChange={(e) => setChatwootForm({ ...chatwootForm, frontendUrl: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                  />
                  <p className="text-[11px] text-muted-foreground">Subdomínio oficial onde o Chatwoot está implantado via Docker.</p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">User / Bot API Access Token</label>
                  <input
                    type="password"
                    placeholder="Token de acesso (Configurações de Perfil > Access Token)"
                    value={chatwootForm.apiAccessToken}
                    onChange={(e) => setChatwootForm({ ...chatwootForm, apiAccessToken: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs sm:text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                  />
                  <p className="text-[11px] text-muted-foreground">Token de usuário de sistema com permissões de administrador no Chatwoot.</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-foreground">Account ID</label>
                    <input
                      type="text"
                      placeholder="1"
                      value={chatwootForm.accountId}
                      onChange={(e) => setChatwootForm({ ...chatwootForm, accountId: e.target.value })}
                      className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs sm:text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                    />
                    <p className="text-[11px] text-muted-foreground">ID da conta (padrão: 1).</p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-foreground">Inbox ID (WhatsApp)</label>
                    <input
                      type="number"
                      placeholder="1"
                      value={chatwootForm.inboxId}
                      onChange={(e) => setChatwootForm({ ...chatwootForm, inboxId: Number(e.target.value) || 1 })}
                      className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs sm:text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                    />
                    <p className="text-[11px] text-muted-foreground">ID da caixa de entrada do WhatsApp.</p>
                  </div>
                </div>

                <div className="pt-2 flex items-center justify-between border-t border-border/60">
                  <span className="text-xs font-semibold text-muted-foreground">Habilitar envio ativo de documentos</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chatwootForm.isEnabled}
                      onChange={(e) => setChatwootForm({ ...chatwootForm, isEnabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* PAINEL 2: Meta WhatsApp Business Cloud API */}
            <Card className="rounded-3xl border border-border shadow-xs bg-card">
              <CardHeader className="border-b border-border/70 pb-3">
                <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-emerald-600" />
                  <span>2. Meta WhatsApp Business Cloud API Oficial</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-foreground">Phone Number ID (Meta)</label>
                    <input
                      type="text"
                      placeholder="Ex: 104928374829102"
                      value={chatwootForm.phoneNumberId}
                      onChange={(e) => setChatwootForm({ ...chatwootForm, phoneNumberId: e.target.value })}
                      className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-foreground">WABA ID (Account ID)</label>
                    <input
                      type="text"
                      placeholder="Ex: 928374829102938"
                      value={chatwootForm.businessAccountId}
                      onChange={(e) => setChatwootForm({ ...chatwootForm, businessAccountId: e.target.value })}
                      className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">Número Oficial Verificado (WhatsApp)</label>
                  <input
                    type="text"
                    placeholder="+55 31 99999-8888"
                    value={chatwootForm.verifiedPhoneNumber}
                    onChange={(e) => setChatwootForm({ ...chatwootForm, verifiedPhoneNumber: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs sm:text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                  />
                  <p className="text-[11px] text-muted-foreground">Número com selo corporativo e certificado de exibição Medikei Saúde.</p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">Token Permanente da Meta (System User)</label>
                  <input
                    type="password"
                    placeholder="EAA..."
                    value={chatwootForm.permanentAccessToken}
                    onChange={(e) => setChatwootForm({ ...chatwootForm, permanentAccessToken: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                  />
                  <p className="text-[11px] text-muted-foreground">Gerado no Meta Business Manager com permissão <code className="text-xs bg-muted px-1 rounded">whatsapp_business_messaging</code>.</p>
                </div>

                <div className="p-3 bg-muted/40 rounded-xl border border-border/80 space-y-1 text-xs">
                  <span className="font-bold text-foreground block">Webhook Callback URL (Para cadastrar no App da Meta):</span>
                  <div className="flex items-center justify-between gap-2 font-mono text-[11px] text-muted-foreground bg-background p-2 rounded-lg border border-border">
                    <span className="truncate">{chatwootForm.frontendUrl}/webhooks/whatsapp</span>
                    <button
                      type="button"
                      onClick={() => handleCopySnippet(`${chatwootForm.frontendUrl}/webhooks/whatsapp`, 'webhook')}
                      className="text-primary hover:underline font-sans font-bold shrink-0 cursor-pointer"
                    >
                      {copiedSnippet === 'webhook' ? 'Copiado!' : 'Copiar'}
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* PAINEL 3: Template Oficial Meta & Live WhatsApp Preview */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            {/* Especificações do Template (7 cols) */}
            <Card className="lg:col-span-7 rounded-3xl border border-border shadow-xs bg-card flex flex-col justify-between">
              <CardHeader className="border-b border-border/70 pb-3">
                <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  <span>3. Template Homologado na Meta (Categoria UTILITY)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div className="p-3 rounded-xl bg-muted/40 border border-border">
                    <span className="text-[10px] uppercase font-bold text-muted-foreground block">Nome do Template</span>
                    <strong className="text-xs font-mono text-foreground mt-0.5 block">{chatwootForm.templateName}</strong>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/40 border border-border">
                    <span className="text-[10px] uppercase font-bold text-muted-foreground block">Categoria Meta</span>
                    <strong className="text-xs font-bold text-emerald-600 mt-0.5 block">UTILITY (Menor custo)</strong>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/40 border border-border">
                    <span className="text-[10px] uppercase font-bold text-muted-foreground block">Cabeçalho (Header)</span>
                    <strong className="text-xs font-bold text-blue-600 mt-0.5 block">DOCUMENT (PDF Anexo)</strong>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">Identificador do Template na Meta</label>
                  <input
                    type="text"
                    value={chatwootForm.templateName}
                    onChange={(e) => setChatwootForm({ ...chatwootForm, templateName: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                  />
                  <p className="text-[11px] text-muted-foreground">Deve coincidir exatamente com o nome cadastrado no WhatsApp Manager da Meta.</p>
                </div>

                <div className="p-3.5 bg-muted/30 rounded-2xl border border-border text-xs space-y-2">
                  <span className="font-bold text-foreground block">Corpo Oficial do Template (Aprovado na Meta):</span>
                  <div className="bg-background p-3 rounded-xl border border-border text-xs font-medium text-muted-foreground leading-relaxed">
                    <p>Olá, <strong className="text-primary font-mono">&#123;&#123;1&#125;&#125;</strong>!</p>
                    <p className="mt-2">
                      O(A) Dr(a). <strong className="text-primary font-mono">&#123;&#123;2&#125;&#125;</strong> disponibilizou o seu documento médico (<strong className="text-primary font-mono">&#123;&#123;3&#125;&#125;</strong>) através da Medikei Saúde.
                    </p>
                    <p className="mt-2">
                      O arquivo oficial assinado digitalmente segue anexado a esta mensagem. Caso precise de suporte sobre sua consulta, basta nos responder por aqui.
                    </p>
                    <p className="mt-3 text-[10px] text-muted-foreground border-t border-border pt-1">
                      Medikei Saúde • Cuidado que vai até você
                    </p>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Variáveis mapeadas: <code className="bg-muted px-1 rounded">&#123;&#123;1&#125;&#125;</code>: Nome do Paciente | <code className="bg-muted px-1 rounded">&#123;&#123;2&#125;&#125;</code>: Médico | <code className="bg-muted px-1 rounded">&#123;&#123;3&#125;&#125;</code>: Tipo do Documento
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Live WhatsApp Preview (5 cols) */}
            <Card className="lg:col-span-5 rounded-3xl border border-border shadow-xs bg-slate-900 text-white flex flex-col justify-between overflow-hidden">
              <CardHeader className="border-b border-white/10 pb-3 bg-slate-950/80">
                <CardTitle className="text-xs font-bold text-emerald-400 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Smartphone className="w-3.5 h-3.5" />
                    <span>Pré-Visualização no WhatsApp do Paciente</span>
                  </span>
                  <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-500/30">
                    Live Preview
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-5 flex-1 flex flex-col justify-center">
                {/* Balão estilizado do WhatsApp */}
                <div className="bg-[#128C7E]/20 p-3.5 rounded-2xl border border-[#128C7E]/30 space-y-3 shadow-lg">
                  {/* Header do Documento Anexo no WhatsApp */}
                  <div className="p-3 bg-slate-800/90 rounded-xl border border-white/10 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-red-500/20 text-red-400 flex items-center justify-center shrink-0 border border-red-500/30">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-white truncate">Receita_Medica_Digital.pdf</p>
                      <p className="text-[10px] text-gray-400">1 página • PDF com ICP-Brasil</p>
                    </div>
                  </div>

                  {/* Texto da Mensagem */}
                  <div className="text-xs text-gray-100 leading-relaxed space-y-1.5">
                    <p>Olá, <strong className="text-emerald-300 font-bold">{testPatientName}</strong>!</p>
                    <p>
                      O(A) Dr(a). <strong className="text-emerald-300 font-bold">{testDoctorName}</strong> disponibilizou o seu documento médico (<strong className="text-white font-bold">{testDocType}</strong>) através da Medikei Saúde.
                    </p>
                    <p>
                      O arquivo oficial assinado digitalmente segue anexado a esta mensagem. Caso precise de suporte sobre sua consulta, basta nos responder por aqui.
                    </p>
                  </div>

                  <div className="flex items-center justify-between text-[10px] text-gray-400 pt-1 border-t border-white/10">
                    <span>Medikei Saúde • Cuidado que vai até você</span>
                    <span className="text-[9px]">14:32 ✓✓</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* PAINEL 4: Sandbox de Teste de Disparo em Tempo Real */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card">
            <CardHeader className="border-b border-border/70 pb-3">
              <CardTitle className="text-sm font-bold text-foreground flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Play className="w-4 h-4 text-emerald-600" />
                  <span>4. Sandbox de Teste de Disparo em Tempo Real</span>
                </span>
                <span className="text-xs text-muted-foreground font-normal">Simulação direta via Chatwoot &amp; Meta Graph API</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">Telefone de Teste (DDI + DDD + Num)</label>
                  <input
                    type="text"
                    placeholder="+5531999998888"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground font-bold"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">Nome do Paciente</label>
                  <input
                    type="text"
                    value={testPatientName}
                    onChange={(e) => setTestPatientName(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">Nome do Médico</label>
                  <input
                    type="text"
                    value={testDoctorName}
                    onChange={(e) => setTestDoctorName(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">Tipo de Documento</label>
                  <select
                    value={testDocType}
                    onChange={(e) => setTestDocType(e.target.value as any)}
                    className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                  >
                    <option value="Receita Médica">Receita Médica</option>
                    <option value="Atestado">Atestado Médico</option>
                    <option value="Pedido de Exame">Pedido de Exame</option>
                    <option value="Prontuário">Resumo de Prontuário</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-1">
                <input
                  type="text"
                  placeholder="URL pública do PDF (ex: https://storage.medikei.com.br/demo/receita.pdf)"
                  value={testPdfUrl}
                  onChange={(e) => setTestPdfUrl(e.target.value)}
                  className="flex-1 h-10 px-3 rounded-xl border border-input bg-background text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground w-full"
                />

                <Button
                  onClick={handleTestChatwootDispatch}
                  disabled={isTestingChatwootDispatch || !testPhone.trim()}
                  className="w-full sm:w-auto h-10 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-sm gap-2 shrink-0 cursor-pointer"
                >
                  {isTestingChatwootDispatch ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  <span>Disparar Documento de Teste via WhatsApp</span>
                </Button>
              </div>

              {/* Resultado do Teste de Disparo */}
              {chatwootDispatchResult && (
                <div className="p-4 rounded-2xl bg-muted/30 border border-border text-xs space-y-2">
                  <span className="font-bold text-foreground block">Retorno da API Chatwoot:</span>
                  <pre className="p-3 bg-gray-900 text-gray-100 rounded-xl font-mono text-[11px] overflow-x-auto">
                    {JSON.stringify(chatwootDispatchResult, null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>

          {/* PAINEL 5: Guia Rápido de Deploy & Infraestrutura (Docker Compose & .env) */}
          <Card className="rounded-3xl border border-border shadow-xs bg-card">
            <CardHeader className="border-b border-border/70 pb-3">
              <CardTitle className="text-sm font-bold text-foreground flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-primary" />
                  <span>5. Guia de Deploy na VPS (Docker Compose &amp; Arquivo .env)</span>
                </span>
                <span className="text-xs text-muted-foreground font-normal">Comandos prontos para produção</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-bold text-foreground">
                    <span>1. docker-compose.yaml</span>
                    <button
                      type="button"
                      onClick={() => handleCopySnippet(`version: '3.8'
services:
  base: &base
    image: chatwoot/chatwoot:latest
    env_file: .env
    volumes:
      - storage_data:/app/storage

  rails:
    <<: *base
    container_name: medikei_chatwoot_rails
    depends_on:
      - postgres
      - redis
    ports:
      - '127.0.0.1:3000:3000'
    environment:
      - NODE_ENV=production
      - RAILS_ENV=production
    entrypoint: docker/entrypoints/rails.sh
    command: ['bundle', 'exec', 'rails', 's', '-p', '3000', '-b', '0.0.0.0']
    restart: always

  sidekiq:
    <<: *base
    container_name: medikei_chatwoot_sidekiq
    depends_on:
      - postgres
      - redis
    environment:
      - NODE_ENV=production
      - RAILS_ENV=production
    command: ['bundle', 'exec', 'sidekiq', '-C', 'config/sidekiq.yml']
    restart: always

  postgres:
    image: postgres:15-alpine
    container_name: medikei_chatwoot_postgres
    restart: always
    volumes:
      - postgres_data:/var/lib/postgresql/data
    environment:
      POSTGRES_DB: chatwoot_production
      POSTGRES_USER: chatwoot
      POSTGRES_PASSWORD: \${POSTGRES_PASSWORD}

  redis:
    image: redis:7-alpine
    container_name: medikei_chatwoot_redis
    restart: always
    command: ["redis-server", "--requirepass", "\${REDIS_PASSWORD}"]
    volumes:
      - redis_data:/data

volumes:
  postgres_data:
  redis_data:
  storage_data:`, 'docker')}
                      className="text-primary hover:underline text-xs flex items-center gap-1 cursor-pointer"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      <span>{copiedSnippet === 'docker' ? 'Copiado!' : 'Copiar'}</span>
                    </button>
                  </div>
                  <pre className="p-3 bg-gray-900 text-gray-100 rounded-xl font-mono text-[11px] overflow-x-auto max-h-48">
{`mkdir -p /opt/medikei/chatwoot && cd /opt/medikei/chatwoot
docker compose run --rm rails bundle exec rails db:chatwoot_prepare
docker compose up -d`}
                  </pre>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-bold text-foreground">
                    <span>2. Arquivo .env</span>
                    <button
                      type="button"
                      onClick={() => handleCopySnippet(`FRONTEND_URL=https://chat.medikei.com.br
SECRET_KEY_BASE=\${SECRET_KEY_BASE}
FORCE_SSL=true
POSTGRES_HOST=postgres
POSTGRES_DATABASE=chatwoot_production
POSTGRES_USERNAME=chatwoot
POSTGRES_PASSWORD=DefinaUmaSenhaFortePostgres123!
REDIS_URL=redis://default:DefinaUmaSenhaForteRedis123!@redis:6379/1
REDIS_PASSWORD=DefinaUmaSenhaForteRedis123!
ACTIVE_STORAGE_SERVICE=local
MAILER_SENDER_EMAIL=suporte@medikei.com.br`, 'env')}
                      className="text-primary hover:underline text-xs flex items-center gap-1 cursor-pointer"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      <span>{copiedSnippet === 'env' ? 'Copiado!' : 'Copiar'}</span>
                    </button>
                  </div>
                  <pre className="p-3 bg-gray-900 text-gray-100 rounded-xl font-mono text-[11px] overflow-x-auto max-h-48">
{`FRONTEND_URL=https://chat.medikei.com.br
SECRET_KEY_BASE=(gerado com openssl rand -hex 64)
FORCE_SSL=true
POSTGRES_HOST=postgres
POSTGRES_DATABASE=chatwoot_production
POSTGRES_USERNAME=chatwoot
POSTGRES_PASSWORD=DefinaUmaSenhaFortePostgres123!
REDIS_URL=redis://default:DefinaUmaSenhaForteRedis123!@redis:6379/1
REDIS_PASSWORD=DefinaUmaSenhaForteRedis123!
ACTIVE_STORAGE_SERVICE=local`}
                  </pre>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

interface ConfigItemProps {
  config?: PlatformConfig;
  label?: string;
  description?: string;
  editingKey: string | null;
  editValue: string;
  onEdit: (config: PlatformConfig) => void;
  onSave: () => void;
  onCancel: () => void;
  onEditChange: (value: string) => void;
  formatFn: (value: any) => string;
}

function ConfigItem({
  config,
  label,
  description,
  editingKey,
  editValue,
  onEdit,
  onSave,
  onCancel,
  onEditChange,
  formatFn,
}: ConfigItemProps) {
  if (!config && !label) return null;

  const displayLabel = label || config?.label || '';
  const displayDesc = description || config?.description;
  const displayValue = config?.value;
  const isEditing = editingKey === config?.key;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between py-3.5 border-b border-border/60 last:border-b-0 gap-3">
      <div className="flex-1">
        <h4 className="font-semibold text-sm sm:text-base text-foreground">{displayLabel}</h4>
        {displayDesc && <p className="text-xs text-muted-foreground mt-0.5">{displayDesc}</p>}
      </div>
      <div className="flex items-center gap-2 self-start sm:self-auto">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                value={editValue}
                onChange={(e) => onEditChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSave();
                  if (e.key === 'Escape') onCancel();
                }}
                className="w-28 sm:w-32 px-3 py-1.5 text-sm bg-background border border-primary/40 focus:border-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 text-foreground font-semibold"
                placeholder="Valor"
                autoFocus
              />
            </div>
            <button
              type="button"
              onClick={onSave}
              className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer flex items-center gap-1"
            >
              <Check className="w-3.5 h-3.5" />
              <span>Salvar</span>
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-xl text-xs font-semibold transition-all cursor-pointer"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            <span className="font-bold text-sm text-foreground bg-muted/50 px-3 py-1 rounded-xl border border-border/80">
              {formatFn(displayValue)}
            </span>
            <button
              type="button"
              onClick={() => config && onEdit(config)}
              className="p-1.5 sm:px-3 sm:py-1.5 text-xs font-semibold text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-xl transition-all cursor-pointer flex items-center gap-1"
            >
              <Pencil className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Editar</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
